import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import xmljs from 'xml-js';
import moment from 'moment';
import _ from 'lodash';
import consent from '@/store/modules/consent';

// Print Project version & Environment in console
console.log(
  process.env.VUE_APP_VERSION + ':' + process.env.VUE_APP_ENV.toLowerCase()
);

axios.defaults.withCredentials = true;
Vue.use(Vuex);
const API_URL = process.env.VUE_APP_API_URL;
const VEHICLE_IMG_BASE_URL = process.env.VUE_APP_PORSCHE_PIC_BASE_URL;

// Shared coverage helpers. Previously duplicated in every state-specific
// coverage component (ArizonaCoverages, UtahCoverages, ...) and QuoteCoverage.
// Defined at module scope so they can be reused by mutations/actions in this
// store and exposed to components via getters.
const isThisSquish = vin => {
  const regex = RegExp('/*0000000');
  const validVinRegex = RegExp('/[ioq]+', 'gi');

  return !!(
    regex.test(vin) ||
    vin.length !== 17 ||
    vin.includes('*') ||
    vin.includes('!') ||
    validVinRegex.test(vin)
  );
};

const isFloat = value => {
  if (value) {
    return Number(value) === value && value % 1 !== 0;
  }
};

const roundAmountTo = (number, digits) => {
  if (digits === undefined) {
    digits = 0;
  }
  const multiplier = Math.pow(10, digits);
  number = parseFloat((number * multiplier).toFixed(11));
  return Math.round(number) / multiplier;
};

const restructureDriverObject = (state, incomingQuoteRequest) => {
  let drivers = [];
  const driversListFromAPI = incomingQuoteRequest.drivers;
  for (let i = 0; i < driversListFromAPI.length; i++) {
    let formattedDriverDetail = {};
    const thisDriver = driversListFromAPI[i];
    const moreDrivers = i + 1 >= driversListFromAPI.length ? false : true;
    if (i === 0) {
      // Primary Driver
      formattedDriverDetail = {
        accidentsConvictions: state.selects.drivers.accidentsConvictions.find(
          violation => violation.value === thisDriver.violations
        ),
        addAnotherDriver: !thisDriver.isMarried && moreDrivers ? 'Yes' : 'No',
        dateOfBirth: moment(thisDriver.dob).format('MM/DD/YYYY'),
        emailAddress: incomingQuoteRequest.emailAddress,
        firstName: thisDriver.firstName,
        gender: state.selects.drivers.gender.find(
          obj => obj.value === thisDriver.gender
        ),
        hasAutoInsurance: state.selects.drivers.autoInsurance.find(
          obj => obj.value === incomingQuoteRequest.priorCoverage
        ),
        homeRentalStatus: state.selects.drivers.homeRentalStatus.find(
          obj => obj.value === incomingQuoteRequest.isHomeOwner
        ),
        id: i + 1,
        lastName: thisDriver.lastName,
        levelOfEducation: state.selects.drivers.levelOfEducation.find(
          education => education.value === thisDriver.educationCode
        ),
        licenseNum: thisDriver.licenseNum,
        licenseState: state.selects.drivers.licenseStates.find(
          state => state.value === thisDriver.licenseState
        ),
        licenseStatus: state.selects.drivers.licenseStatus.find(
          obj => obj.value === thisDriver.licenseStatus
        ),
        DLValidation: 'required|DL#' + thisDriver.licenseState,
        maritalStatus:
          state.selects.drivers.maritalStatus[Number(thisDriver.isMarried)],
        previousExpirationDate: incomingQuoteRequest.previousExpirationDate
          ? moment(incomingQuoteRequest.previousExpirationDate).format(
              'MM/DD/YYYY'
            )
          : '',
        recentCoverageLimits:
          incomingQuoteRequest.priorCoverage === 3 ||
          incomingQuoteRequest.priorCoverage === 1
            ? state.selects.drivers.recentCoverageLimits.find(
                obj => obj.value === incomingQuoteRequest.priorBiLimit
              )
            : null,
        recentIssuerLength: state.selects.drivers.recentIssuerLengths.find(
          obj => obj.value === incomingQuoteRequest.priorYearsInsured
        ),
        // Primary driver specific
        isPrimaryDriver: true,
        name: 'Your Drivers',
        relation: 'INSURED',
        status: 'RATED',
        showLengthWithInsurer: !!(
          incomingQuoteRequest.previousExpirationDate &&
          incomingQuoteRequest.previousExpirationDate !== ''
        )
      };
      if (
        (formattedDriverDetail.recentCoverageLimits === null ||
          formattedDriverDetail.recentCoverageLimits === undefined) &&
        incomingQuoteRequest.priorBiLimit !== null
      ) {
        formattedDriverDetail.recentCoverageLimits =
          incomingQuoteRequest.priorBiLimit &&
          incomingQuoteRequest.priorBiLimit.includes('250/500')
            ? state.selects.drivers.recentCoverageLimits.find(
                obj => obj.value === '250/500'
              )
            : state.selects.drivers.recentCoverageLimits[
                state.selects.drivers.recentCoverageLimits.length - 1
              ];
      }
      if (thisDriver.occupationCode) {
        formattedDriverDetail.occupationCode = thisDriver.occupationCode;
      }
      drivers.push(formattedDriverDetail);
    } else if (drivers[i - 1] && drivers[i - 1].maritalStatus === 'Married') {
      formattedDriverDetail = {
        addAnotherDriver: moreDrivers ? 'Yes' : 'No',
        dateOfBirth: moment(thisDriver.dob).format('MM/DD/YYYY'),
        firstName: thisDriver.firstName,
        gender: state.selects.drivers.gender.find(
          obj => obj.value === thisDriver.gender
        ),
        id: i + 1,
        lastName: thisDriver.lastName,
        levelOfEducation: state.selects.drivers.levelOfEducation.find(
          education => education.value === thisDriver.educationCode
        ),
        // spouse/ additional driver spouse specific
        isPrimaryDriver: false,
        name: i + 1 === 2 ? 'Your Spouse' : 'Additional Driver Spouse',
        relation: 'Spouse',
        isExcluded: state.selects.drivers.excludeSpouseOptions.find(
          option => option.value === thisDriver.isExcluded
        )
      };
      if (thisDriver.status === 'RATED') {
        // Rated Spouse of Primary Driver
        formattedDriverDetail = {
          ...formattedDriverDetail,
          accidentsConvictions: state.selects.drivers.accidentsConvictions.find(
            violation => violation.value === thisDriver.violations
          ),
          licenseNum: thisDriver.licenseNum,
          licenseState: state.selects.drivers.licenseStates.find(
            state => state.value === thisDriver.licenseState
          ),
          licenseStatus: state.selects.drivers.licenseStatus.find(
            obj => obj.value === thisDriver.licenseStatus
          ),
          status: 'RATED',
          DLValidation: 'required|DL#' + thisDriver.licenseState
        };
      } else {
        // Listed Spouse of Primary Driver
        formattedDriverDetail.status = 'LIST';
      }
      if (thisDriver.occupationCode) {
        formattedDriverDetail.occupationCode = thisDriver.occupationCode;
      }
      drivers.push(formattedDriverDetail);
    } else {
      // Additional driver
      formattedDriverDetail = {
        accidentsConvictions: state.selects.drivers.accidentsConvictions.find(
          violation => violation.value === thisDriver.violations
        ),
        addAnotherDriver: moreDrivers ? 'Yes' : 'No',
        dateOfBirth: moment(thisDriver.dob).format('MM/DD/YYYY'),
        firstName: thisDriver.firstName,
        gender: state.selects.drivers.gender.find(
          obj => obj.value === thisDriver.gender
        ),
        id: i + 1,
        lastName: thisDriver.lastName,
        levelOfEducation: state.selects.drivers.levelOfEducation.find(
          education => education.value === thisDriver.educationCode
        ),
        licenseNum: thisDriver.licenseNum,
        licenseState: state.selects.drivers.licenseStates.find(
          state => state.value === thisDriver.licenseState
        ),
        licenseStatus: state.selects.drivers.licenseStatus.find(
          obj => obj.value === thisDriver.licenseStatus
        ),
        DLValidation: 'required|DL#' + thisDriver.licenseState,
        maritalStatus:
          state.selects.drivers.maritalStatus[Number(thisDriver.isMarried)],
        // Additional driver specific
        isPrimaryDriver: false,
        name: 'Additional Driver',
        relation: state.selects.drivers.relation.find(
          val => val.toUpperCase() === thisDriver.relation
        ),
        status: 'RATED'
      };
      if (thisDriver.occupationCode) {
        formattedDriverDetail.occupationCode = thisDriver.occupationCode;
      }
      drivers.push(formattedDriverDetail);
    }
  }
  return drivers;
};

// Helpers for the partner quote-request prefill schema
// (see src/mocks/exampleQuoteRequestDetails.json).
const formatLimitWithCommas = limit =>
  ('' + limit).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

const formatSingleLimit = limit =>
  limit ? '$' + formatLimitWithCommas(limit) : null;

const formatSlashLimit = limit =>
  limit
    ? limit
        .split('/')
        .map(part => '$' + formatLimitWithCommas(part))
        .join('/')
    : null;

const formatPrefillPhoneNumber = number => {
  if (!number) {
    return null;
  }
  const digits = ('' + number).replace(/\D/g, '');
  if (digits.length !== 10) {
    return number;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Maps the partner quote-request `drivers` array into the app's
// quoteForm.drivers shape. Mirrors the structure produced by
// restructureDriverObject, but reads from the prefill schema.
const restructureDriversFromQuoteRequest = (state, details) => {
  const drivers = [];
  const driversList = details.drivers || [];
  const householdInfo = details.householdInfo || {};
  const priorInsurance = details.priorInsurance || {};

  const priorBiLimit = (() => {
    const priorBi = (priorInsurance.priorLimits || []).find(
      limit => limit.coverageCode === 'BI'
    );
    if (!priorBi || !priorBi.limit) {
      return null;
    }
    // Schema gives full limits (e.g. "100000/300000"); the select options
    // use the abbreviated thousands form (e.g. "100/300").
    return priorBi.limit
      .split('/')
      .map(val => (Number(val) / 1000).toString())
      .join('/');
  })();

  const hasAutoInsuranceValue = priorInsurance.priorStatus
    ? priorInsurance.priorStatus === 'in_force'
      ? 3
      : 2
    : null;

  const previousExpirationDate = priorInsurance.expirationDate
    ? moment(priorInsurance.expirationDate).format('MM/DD/YYYY')
    : '';

  const recentIssuerLengthValue = (() => {
    const months = priorInsurance.priorMonthsInsured;
    if (months === undefined || months === null) {
      return null;
    }
    if (months < 6) {
      return 0;
    }
    if (months < 36) {
      return 1;
    }
    return 2;
  })();

  const genderLabelFor = driver =>
    driver.gender
      ? driver.gender.charAt(0).toUpperCase() + driver.gender.slice(1)
      : null;

  const accidentsFor = driver =>
    state.selects.drivers.accidentsConvictions.find(
      violation => violation.value === Math.min(driver.atFaults || 0, 3)
    );

  for (let i = 0; i < driversList.length; i++) {
    const thisDriver = driversList[i];
    const moreDrivers = i + 1 < driversList.length;
    const isMarried = !!thisDriver.married;
    const isRated = (thisDriver.driverType || '').toUpperCase() === 'RATED';
    let formattedDriverDetail = {};

    if (i === 0) {
      // Primary driver
      formattedDriverDetail = {
        accidentsConvictions: accidentsFor(thisDriver),
        addAnotherDriver: !isMarried && moreDrivers ? 'Yes' : 'No',
        dateOfBirth: thisDriver.birthDate
          ? moment(thisDriver.birthDate).format('MM/DD/YYYY')
          : null,
        emailAddress: householdInfo.policyEmail,
        firstName: thisDriver.firstName,
        gender: state.selects.drivers.gender.find(
          obj => obj.value === genderLabelFor(thisDriver)
        ),
        hasAutoInsurance: state.selects.drivers.autoInsurance.find(
          obj => obj.value === hasAutoInsuranceValue
        ),
        homeRentalStatus: state.selects.drivers.homeRentalStatus.find(
          obj => obj.value === householdInfo.isHomeOwner
        ),
        id: i + 1,
        lastName: thisDriver.lastName,
        levelOfEducation: state.selects.drivers.levelOfEducation.find(
          education => education.value === thisDriver.education
        ),
        licenseNum: thisDriver.licenseNumber,
        licenseState: state.selects.drivers.licenseStates.find(
          opt => opt.value === thisDriver.licenseState
        ),
        licenseStatus: state.selects.drivers.licenseStatus.find(
          obj => obj.value === 'ACTIVE'
        ),
        DLValidation: 'required|DL#' + thisDriver.licenseState,
        maritalStatus: state.selects.drivers.maritalStatus[Number(isMarried)],
        previousExpirationDate,
        recentCoverageLimits: priorBiLimit
          ? state.selects.drivers.recentCoverageLimits.find(
              obj => obj.value === priorBiLimit
            )
          : null,
        recentIssuerLength: state.selects.drivers.recentIssuerLengths.find(
          obj => obj.value === recentIssuerLengthValue
        ),
        isPrimaryDriver: true,
        name: 'Your Drivers',
        relation: 'INSURED',
        status: 'RATED',
        showLengthWithInsurer: !!previousExpirationDate
      };
    } else if (drivers[i - 1] && drivers[i - 1].maritalStatus === 'Married') {
      // Spouse of the previous (married) driver
      formattedDriverDetail = {
        addAnotherDriver: moreDrivers ? 'Yes' : 'No',
        dateOfBirth: thisDriver.birthDate
          ? moment(thisDriver.birthDate).format('MM/DD/YYYY')
          : null,
        firstName: thisDriver.firstName,
        gender: state.selects.drivers.gender.find(
          obj => obj.value === genderLabelFor(thisDriver)
        ),
        id: i + 1,
        lastName: thisDriver.lastName,
        levelOfEducation: state.selects.drivers.levelOfEducation.find(
          education => education.value === thisDriver.education
        ),
        isPrimaryDriver: false,
        name: i + 1 === 2 ? 'Your Spouse' : 'Additional Driver Spouse',
        relation: 'Spouse',
        isExcluded: state.selects.drivers.excludeSpouseOptions.find(
          option => option.value === false
        )
      };
      if (isRated) {
        formattedDriverDetail = {
          ...formattedDriverDetail,
          accidentsConvictions: accidentsFor(thisDriver),
          licenseNum: thisDriver.licenseNumber,
          licenseState: state.selects.drivers.licenseStates.find(
            opt => opt.value === thisDriver.licenseState
          ),
          licenseStatus: state.selects.drivers.licenseStatus.find(
            obj => obj.value === 'ACTIVE'
          ),
          status: 'RATED',
          DLValidation: 'required|DL#' + thisDriver.licenseState
        };
      } else {
        formattedDriverDetail.status = 'LIST';
      }
    } else {
      // Additional driver
      formattedDriverDetail = {
        accidentsConvictions: accidentsFor(thisDriver),
        addAnotherDriver: moreDrivers ? 'Yes' : 'No',
        dateOfBirth: thisDriver.birthDate
          ? moment(thisDriver.birthDate).format('MM/DD/YYYY')
          : null,
        firstName: thisDriver.firstName,
        gender: state.selects.drivers.gender.find(
          obj => obj.value === genderLabelFor(thisDriver)
        ),
        id: i + 1,
        lastName: thisDriver.lastName,
        levelOfEducation: state.selects.drivers.levelOfEducation.find(
          education => education.value === thisDriver.education
        ),
        licenseNum: thisDriver.licenseNumber,
        licenseState: state.selects.drivers.licenseStates.find(
          opt => opt.value === thisDriver.licenseState
        ),
        licenseStatus: state.selects.drivers.licenseStatus.find(
          obj => obj.value === 'ACTIVE'
        ),
        DLValidation: 'required|DL#' + thisDriver.licenseState,
        maritalStatus: state.selects.drivers.maritalStatus[Number(isMarried)],
        isPrimaryDriver: false,
        name: 'Additional Driver',
        relation: state.selects.drivers.relation.find(
          val =>
            val.toUpperCase() ===
            (thisDriver.driverRelation || '').toUpperCase()
        ),
        status: 'RATED'
      };
    }

    if (thisDriver.middleInitial) {
      formattedDriverDetail.middleInitial = thisDriver.middleInitial;
    }
    if (thisDriver.occupation && thisDriver.occupation.occupation) {
      formattedDriverDetail.occupationCode = thisDriver.occupation.occupation;
    }
    drivers.push(formattedDriverDetail);
  }
  return drivers;
};

export default new Vuex.Store({
  modules: {
    consent
  },
  state: {
    activeNav: false,
    activeUserNav: false,
    status: '',
    loader: {
      isLoading: false,
      loaderText: ''
    },
    isLoggedIn: false,
    isRollover: false,
    accountIdentifier: '',
    user: {},
    policies: {},
    selectedPolicy: null,
    selectedVehicle: null,
    quoteForm: {
      info: {},
      vehicles: [],
      drivers: [],
      coverage: {}
    },
    lienHolderData: [],
    lienHolderResponses: [],
    quoteResponse: null,
    bindResponse: null,
    quoteRequest: null,
    vehicleImageResponse: null,
    selects: {
      info: {
        estimatedCreditScore: [
          {
            label: 'Excellent',
            value: 800
          },
          {
            label: 'Pretty Good',
            value: 780
          },
          {
            label: 'Just OK',
            value: 595
          },
          {
            label: 'Not Very Good',
            value: 550
          }
        ],
        isGarageSameAsMailing: [
          {
            label: 'Yes',
            value: 1
          },
          {
            label: 'No',
            value: 2
          }
        ],
        states: [
          {
            label: 'Arizona',
            value: 'AZ'
          },
          {
            label: 'Colorado',
            value: 'CO'
          },
          {
            label: 'Florida',
            value: 'FL'
          },
          {
            label: 'Georgia',
            value: 'GA'
          },
          {
            label: 'Illinois',
            value: 'IL'
          },
          {
            label: 'Oregon',
            value: 'OR'
          },
          {
            label: 'Tennessee',
            value: 'TN'
          },
          {
            label: 'Texas',
            value: 'TX'
          },
          {
            label: 'Utah',
            value: 'UT'
          }
        ],
        phoneType: [
          {
            label: 'Home',
            value: 'Home'
          },
          {
            label: 'Mobile',
            value: 'Mobile'
          },
          {
            label: 'Work',
            value: 'Work'
          }
        ],
        hearAbout: [
          {
            label: 'Dealer',
            value: 'Dealer'
          },
          {
            label: 'Email from Porsche',
            value: 'Email from Porsche'
          },
          {
            label: 'Brochure from Porsche',
            value: 'Brochure from Porsche'
          },
          {
            label: 'Porsche certified body shop',
            value: 'Porsche certified body shop'
          },
          {
            label: 'Word of mouth',
            value: 'Word of mouth'
          },
          {
            label: 'Search engine',
            value: 'Search engine'
          },
          {
            label: 'Social media',
            value: 'Social media'
          },
          {
            label: 'Press',
            value: 'Press'
          },
          {
            label: 'Porsche Club',
            value: 'Porsche Club'
          }
        ],
        dealers: [
          'Autobahn Porsche',
          'Isringhausen Imports',
          'Loeber Motors',
          'Napleton Westmont Porsche',
          "Napleton's Auto Werks",
          'Park Place Porsche Dallas',
          'Porsche Barrington',
          'Porsche Beaverton',
          'Porsche Bend',
          'Porsche El Paso',
          'Porsche Exchange',
          'Porsche Grapevine',
          'Porsche McKinney',
          'Porsche North Houston',
          'Porsche Orland Park',
          'Porsche Plano',
          'Porsche South Austin',
          'Porsche of Peoria',
          'Porsche of San Antonio'
        ],
        PCAMemberOptions: [
          {
            label: 'Yes',
            value: true
          },
          {
            label: 'No',
            value: false
          }
        ],
        AZ: {
          lengthOfResidency: [
            {
              label: '0 - 11 Months',
              value: '0-11'
            },
            {
              label: '12 or more months',
              value: '12+'
            }
          ]
        },
        CA: {
          lengthOfResidency: [
            {
              label: '0 - 11 Months',
              value: '0-11'
            },
            {
              label: '12 or more months',
              value: '12+'
            }
          ]
        },
        FL: {},
        OH: {
          lengthOfResidency: [
            {
              label: '0 - 11 Months',
              value: '0-11'
            },
            {
              label: '12 or more months',
              value: '12+'
            }
          ]
        },
        PA: {
          lengthOfResidency: [
            {
              label: '0 - 11 Months',
              value: '0-11'
            },
            {
              label: '12 or more months',
              value: '12+'
            }
          ]
        },
        TN: {
          lengthOfResidency: [
            {
              label: '0 - 11 Months',
              value: '0-11'
            },
            {
              label: '12 or more months',
              value: '12+'
            }
          ]
        },
        TX: {
          lengthOfResidency: [
            {
              label: '0 - 11 Months',
              value: '0-11'
            },
            {
              label: '12 or more months',
              value: '12+'
            }
          ]
        },
        UT: {
          lengthOfResidency: [
            {
              label: '0 - 11 Months',
              value: '0-11'
            },
            {
              label: '12 or more months',
              value: '12+'
            }
          ]
        },
        WI: {
          lengthOfResidency: [
            {
              label: '0 - 11 Months',
              value: '0-11'
            },
            {
              label: '12 or more months',
              value: '12+'
            }
          ]
        }
      },
      vehicles: {
        purposes: [
          {
            label: 'Personal',
            value: 'Pleasure'
          },
          {
            label: 'Business',
            value: 'Business'
          }
        ],
        priorDamage: [
          {
            label: 'No',
            value: false
          },
          {
            label: 'Yes',
            value: true
          }
        ],
        leaseOwnFinance: [
          {
            label: 'Leased',
            value: 'leased'
          },
          {
            label: 'Financed',
            value: 'financed'
          },
          {
            label: 'Owned',
            value: 'owned'
          }
        ],
        ownershipLength: [
          {
            label: 'Less Than 1 Year',
            value: '<1'
          },
          {
            label: '1 - 2 Years',
            value: '1-2'
          },
          {
            label: '3 - 4 Years',
            value: '3-4'
          },
          {
            label: '5 or More Years',
            value: '5+'
          }
        ]
      },
      drivers: {
        gender: [
          {
            label: 'Male',
            value: 'Male'
          },
          {
            label: 'Female',
            value: 'Female'
          },
          {
            label: 'Prefer not to say',
            value: 'Unspecified'
          }
        ],
        maritalStatus: ['Single', 'Married'],
        states: [
          {
            label: 'Arizona',
            value: 'AZ'
          },
          {
            label: 'Colorado',
            value: 'CO'
          },
          {
            label: 'California',
            value: 'CA'
          },
          {
            label: 'Florida',
            value: 'FL'
          },
          {
            label: 'Georgia',
            value: 'GA'
          },
          {
            label: 'Illinois',
            value: 'IL'
          },
          {
            label: 'Ohio',
            value: 'OH'
          },
          {
            label: 'Oregon',
            value: 'OR'
          },
          {
            label: 'Pennsylvania',
            value: 'PA'
          },
          {
            label: 'Tennessee',
            value: 'TN'
          },
          {
            label: 'Texas',
            value: 'TX'
          },
          {
            label: 'Wisconsin',
            value: 'WI'
          }
        ],
        licenseStatus: [
          {
            label: 'Current',
            value: 'ACTIVE'
          },
          {
            label: 'Cancelled',
            value: 'EXPIRED'
          },
          {
            label: 'Revoked',
            value: 'REVOKED'
          },
          {
            label: 'Suspended',
            value: 'SUSPENDED'
          }
        ],
        licenseStates: [
          {
            label: 'Alabama',
            value: 'AL'
          },
          {
            label: 'Alaska',
            value: 'AK'
          },
          {
            label: 'Arizona',
            value: 'AZ'
          },
          {
            label: 'Arkansas',
            value: 'AR'
          },
          {
            label: 'California',
            value: 'CA'
          },
          {
            label: 'Colorado',
            value: 'CO'
          },
          {
            label: 'Connecticut',
            value: 'CT'
          },
          {
            label: 'Delaware',
            value: 'DE'
          },
          {
            label: 'District Of Columbia',
            value: 'DC'
          },
          {
            label: 'Florida',
            value: 'FL'
          },
          {
            label: 'Georgia',
            value: 'GA'
          },
          {
            label: 'Hawaii',
            value: 'HI'
          },
          {
            label: 'Idaho',
            value: 'ID'
          },
          {
            label: 'Illinois',
            value: 'IL'
          },
          {
            label: 'Indiana',
            value: 'IN'
          },
          {
            label: 'Iowa',
            value: 'IA'
          },
          {
            label: 'Kansas',
            value: 'KS'
          },
          {
            label: 'Kentucky',
            value: 'KY'
          },
          {
            label: 'Louisiana',
            value: 'LA'
          },
          {
            label: 'Maine',
            value: 'ME'
          },
          {
            label: 'Maryland',
            value: 'MD'
          },
          {
            label: 'Massachusetts',
            value: 'MA'
          },
          {
            label: 'Michigan',
            value: 'MI'
          },
          {
            label: 'Minnesota',
            value: 'MN'
          },
          {
            label: 'Mississippi',
            value: 'MS'
          },
          {
            label: 'Missouri',
            value: 'MO'
          },
          {
            label: 'Montana',
            value: 'MT'
          },
          {
            label: 'Nebraska',
            value: 'NE'
          },
          {
            label: 'Nevada',
            value: 'NV'
          },
          {
            label: 'New Hampshire',
            value: 'NH'
          },
          {
            label: 'New Jersey',
            value: 'NJ'
          },
          {
            label: 'New Mexico',
            value: 'NM'
          },
          {
            label: 'New York',
            value: 'NY'
          },
          {
            label: 'North Carolina',
            value: 'NC'
          },
          {
            label: 'North Dakota',
            value: 'ND'
          },
          {
            label: 'Ohio',
            value: 'OH'
          },
          {
            label: 'Oklahoma',
            value: 'OK'
          },
          {
            label: 'Oregon',
            value: 'OR'
          },
          {
            label: 'Pennsylvania',
            value: 'PA'
          },
          {
            label: 'Rhode Island',
            value: 'RI'
          },
          {
            label: 'South Carolina',
            value: 'SC'
          },
          {
            label: 'South Dakota',
            value: 'SD'
          },
          {
            label: 'Tennessee',
            value: 'TN'
          },
          {
            label: 'Texas',
            value: 'TX'
          },
          {
            label: 'Utah',
            value: 'UT'
          },
          {
            label: 'Vermont',
            value: 'VT'
          },
          {
            label: 'Virginia',
            value: 'VA'
          },
          {
            label: 'Washington',
            value: 'WA'
          },
          {
            label: 'West Virginia',
            value: 'WV'
          },
          {
            label: 'Wisconsin',
            value: 'WI'
          },
          {
            label: 'Wyoming',
            value: 'WY'
          }
        ],
        relation: ['Child', 'Parent', 'Relative', 'Other'],
        homeRentalStatus: [
          {
            label: 'Own',
            value: true
          },
          {
            label: 'Rent',
            value: false
          }
        ],
        levelOfEducation: [
          {
            label: 'Graduate work or graduate degree',
            value: 'GraduateSchool'
          },
          {
            label: 'College degree',
            value: 'CollegeGraduated'
          },
          {
            label: 'Currently in college',
            value: 'CollegeEnrolled'
          },
          {
            label: 'Some college',
            value: 'CollegeNotCompleted'
          },
          {
            label: 'Vocational or Trade School degree',
            value: 'TradeSchool'
          },
          {
            label: 'High school diploma or GED',
            value: 'HighSchool'
          },
          {
            label: 'No high school diploma or GED',
            value: 'NoHighSchool'
          }
        ],
        autoInsurance: [
          {
            label: 'Yes',
            value: 3
          },
          {
            label: 'No, never had',
            value: 0
          },
          {
            label: 'No, it has been less than a month since it lapsed',
            value: 1
          },
          {
            label: 'No, it has been more than a month since it lapsed',
            value: 2
          }
        ],
        recentIssuerLengths: [
          {
            label: 'Less than 6 months',
            value: 0
          },
          {
            label: '6 months - 3 years',
            value: 1
          },
          {
            label: '3+ years',
            value: 2
          }
        ],
        recentCoverageLimits: [
          {
            label: '$25,000/$50,000',
            value: '25/50'
          },
          {
            label: '$50,000/$100,000',
            value: '50/100'
          },
          {
            label: '$100,000/$300,000',
            value: '100/300'
          },
          {
            label: '$250,000/$500,000',
            value: '250/500'
          }
        ],
        accidentsConvictions: [
          {
            label: 'No',
            value: 0
          },
          {
            label: '1',
            value: 1
          },
          {
            label: '2',
            value: 2
          },
          {
            label: '3',
            value: 3
          }
        ],
        addAnotherDriver: ['Yes', 'No'],
        excludeSpouseOptions: [
          {
            label: 'Yes',
            value: true
          },
          {
            label: 'No',
            value: false
          }
        ],
        AZ: {
          recentCoverageLimits: [
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          occupation: [
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: '10' }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: '20' }]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: '30' }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: '40' }]
            },
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                { label: 'Farm/Ranch Owner', value: 'AA4' },
                { label: 'Laborer/Worker', value: 'AA8' },
                { label: 'Supervisor/Manager', value: 'AAE' },
                { label: 'Inspector/Quality Control', value: 'AAG' },
                { label: 'Other - Agriculture/Forestry/Fishing', value: 'AAZ' }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                { label: 'Editor', value: 'ABA' },
                { label: 'Artist/Writer', value: 'ABL' },
                { label: 'Composer/Director/Producer', value: 'ABM' },
                { label: 'Designer/Photographer', value: 'ABN' },
                { label: 'Performer', value: 'ABP' },
                { label: 'Supervisor/Manager', value: 'ABQ' },
                { label: 'Other - Art/Design/Media', value: 'ABZ' }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                { label: 'Analyst', value: 'AC2' },
                { label: 'Appraiser - Real Estate', value: 'AC3' },
                { label: 'Broker', value: 'AC5' },
                { label: 'CSR/Teller', value: 'ACB' },
                { label: 'Manager - Property', value: 'ACL' },
                { label: 'Accountant', value: 'ACS' },
                { label: 'Billing & Collections', value: 'ACT' },
                { label: 'Finance/Insurance/Real Estate Rep', value: 'ACU' },
                { label: 'Financial Advisor/Investor', value: 'ACV' },
                { label: 'Loan Officer', value: 'ACW' },
                { label: 'Sales/Purchasing Rep', value: 'ACX' },
                { label: 'Supervisor/Manager', value: 'ACY' },
                { label: 'Other - Banking/Finance/Real Estate', value: 'ACZ' }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                { label: 'Account Executive', value: 'AD0' },
                { label: 'Administrative Assistant', value: 'AD1' },
                { label: 'Cashier/Checker', value: 'AD3' },
                { label: 'Consultant', value: 'AD5' },
                { label: 'Customer Service Representative', value: 'AD6' },
                { label: 'Accountant/Auditor', value: 'ADR' },
                { label: 'Agent/Broker', value: 'ADS' },
                { label: 'Bookkeeper', value: 'ADV' },
                { label: 'General Office Support', value: 'ADW' },
                { label: 'Human Resource (HR) Specialist', value: 'ADX' },
                { label: 'Manager - Executive/GM', value: 'BD0' },
                { label: 'Manager - Other than Executive/GM', value: 'BD1' },
                { label: 'Manufacturing Rep', value: 'BD2' },
                { label: 'Marketing Specialist', value: 'BD3' },
                { label: 'Receptionist', value: 'BD7' },
                { label: 'Retail Salesperson', value: 'BD8' },
                { label: 'Supervisor/Manager (Retail)', value: 'BDA' },
                { label: 'Supervisor/Manager (Non - Retail)', value: 'BDB' },
                { label: 'Vehicle/Equip Rental Rep', value: 'BDC' },
                { label: 'Other - Sales', value: 'BDZ' },
                { label: 'Other - Business/Office', value: 'CDZ' }
              ]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [
                { label: 'Electrician/Linesman', value: 'AEA' },
                { label: 'Machine/Equipment Operator', value: 'AEC' },
                { label: 'HVAC Technician', value: 'AEG' },
                { label: 'Laborer/Worker', value: 'AEJ' },
                { label: 'Plumber', value: 'AEQ' },
                { label: 'Field Service Technician', value: 'AEU' },
                { label: 'Foreman/Manager/Inspector', value: 'AEV' },
                { label: 'Maintenance/Custodian', value: 'AEW' },
                { label: 'Other - Construction/Energy/Mining', value: 'AEZ' }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                { label: 'Counselor', value: 'AF4' },
                { label: 'Librarian/Curator', value: 'AF7' },
                { label: 'Principal/Administrator', value: 'AF8' },
                { label: 'Teacher, K - 12', value: 'AFD' },
                { label: 'Assistant/Aide', value: 'AFG' },
                { label: 'College Professor', value: 'AFH' },
                { label: 'Teacher - Vocational', value: 'AFK' },
                { label: 'Other - Education/Library', value: 'AFZ' }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer', value: 'AG8' },
                { label: 'Technician', value: 'AGK' },
                { label: 'Analyst - Engineering', value: 'AGL' },
                { label: 'Architect/Drafter', value: 'AGN' },
                { label: 'Scientist/Researcher', value: 'AGP' },
                { label: 'Supervisor/Manager', value: 'AGQ' },
                { label: 'Other - Engineer/Architect', value: 'AGZ' }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [
                { label: 'Bartender', value: 'AH1' },
                { label: 'Caterer', value: 'AH4' },
                { label: 'Chef', value: 'AH5' },
                { label: 'Cook/Worker - Fast Food', value: 'AH9' },
                { label: 'Desk Clerk', value: 'AHB' },
                { label: 'Dishwasher', value: 'AHC' },
                { label: 'Housekeeper/Maid', value: 'AHF' },
                { label: 'Food Preparer/Server', value: 'AHL' },
                { label: 'Manager', value: 'AHM' },
                { label: 'Other - Hotel Services', value: 'AHR' },
                { label: 'Other - Food Services', value: 'AHS' }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                { label: 'Military - Enlisted (E1 - E4)', value: 'AJ9' },
                { label: 'Military - Warrant Officer (W1 - W5)', value: 'AJF' },
                { label: 'US Postal Worker', value: 'AJM' },
                { label: 'Auditor/Analyst/Examiner', value: 'AJN' },
                { label: 'Military - Officer (E5 - O10)', value: 'AJP' },
                { label: 'Supervisor/Manager', value: 'AJQ' },
                { label: 'Other - Government/Military', value: 'AJZ' }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                { label: 'Analyst', value: 'AK1' },
                { label: 'Director/Administrator', value: 'AK3' },
                { label: 'Computer Programmer', value: 'AKA' },
                { label: 'Supervisor/Manager', value: 'AKH' },
                { label: 'Technician', value: 'AKJ' },
                { label: 'Other - Information Technology', value: 'AKZ' }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                { label: 'Agent/Broker', value: 'AL4' },
                { label: 'Claims Adjuster/Investigator', value: 'AL7' },
                { label: 'Supervisor/Manager', value: 'ALJ' },
                { label: 'Underwriter/Actuary', value: 'ALK' },
                { label: 'Other - Insurance', value: 'ALZ' }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Correctional or Probation Officer', value: 'AM4' },
                { label: 'Legal Support Staff', value: 'AMG' },
                { label: 'Security Guard', value: 'AMN' },
                { label: 'Lawyer/Judge/Mediator', value: 'AMQ' },
                { label: 'Fire Fighter', value: 'AMR' },
                { label: 'Police/Highway Patrol/Sheriff', value: 'AMS' },
                { label: 'Detective/Investigator', value: 'AMT' },
                { label: 'Supervisor/Manager', value: 'AMU' },
                { label: 'Other - Legal/Law Enf/Security', value: 'AMZ' }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Chiropractor', value: 'AN2' },
                { label: 'Clergy', value: 'AN3' },
                { label: 'Licensed Nurse (LPN)', value: 'ANE' },
                { label: 'Registered Nurse (RN)', value: 'ANF' },
                { label: 'Paramedic/E.M.T.', value: 'ANK' },
                { label: 'Pharmacist', value: 'ANL' },
                { label: 'Social Worker', value: 'ANQ' },
                { label: 'Veterinarian', value: 'ANU' },
                { label: 'Aide/Attendant', value: 'ANV' },
                { label: 'Assistant/Technician/Therapist', value: 'ANW' },
                { label: 'Dentist/Orthodontist', value: 'ANY' },
                { label: 'Doctor/Surgeon', value: 'BN0' },
                { label: 'Psychologist/Therapist', value: 'BN1' },
                {
                  label: 'Other - Medical/Social Services/Religion',
                  value: 'BNZ'
                }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [
                { label: 'Child or Elderly Care Provider', value: 'AP0' },
                { label: 'Dry Cleaning/Laundry Servicer', value: 'AP1' },
                { label: 'Hair/Personal Care Provider', value: 'AP2' },
                { label: 'Cleaner/Housekeeper', value: 'AP3' },
                { label: 'Pet Service Provider', value: 'AP7' },
                { label: 'Other - Personal Care/Service', value: 'APZ' }
              ]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [
                { label: 'Machine Operator', value: 'AQ7' },
                { label: 'Assembler/Fabricator', value: 'AQJ' },
                { label: 'Inspector/Quality Control', value: 'AQK' },
                { label: 'Supervisor/Manager', value: 'AQP' },
                { label: 'Other - Production/Manufacturing', value: 'AQZ' }
              ]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [
                { label: 'Custodian/Janitor', value: 'AR1' },
                { label: 'Landscape/Grounds Maintenance', value: 'AR7' },
                { label: 'Laborer/Helper', value: 'ARA' },
                { label: 'Repair/Mechanic', value: 'ARB' },
                { label: 'Other - Repair/Maintenance/Grounds', value: 'ARZ' }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                { label: 'Athlete', value: 'AS3' },
                { label: 'Admin/Activity Assistant', value: 'ASJ' },
                { label: 'Fitness Trainer/Instructor', value: 'ASK' },
                { label: 'Supervisor/Manager', value: 'ASL' },
                { label: 'Other - Sports/Recreation', value: 'ASZ' }
              ]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Driver (All Vehicles)', value: 'ATQ' },
                { label: 'Packer/Mover', value: 'ATR' },
                { label: 'Supervisor/Manager', value: 'ATS' },
                { label: 'Other - Travel/Transportation/Storage', value: 'ATZ' }
              ]
            },
            {
              industry: 'Other',
              occupations: []
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ]
        },
        CA: {
          recentCoverageLimits: [
            {
              label: '$15,000/$30,000',
              value: '15/30'
            },
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$30,000/$60,000',
              value: '30/60'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          occupation: [
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: '10' }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: '20' }]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: '30' }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: '40' }]
            },
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                { label: 'Farm/Ranch Owner', value: 'AA4' },
                { label: 'Laborer/Worker', value: 'AA8' },
                { label: 'Supervisor/Manager', value: 'AAE' },
                { label: 'Inspector/Quality Control', value: 'AAG' },
                { label: 'Other - Agriculture/Forestry/Fishing', value: 'AAZ' }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                { label: 'Editor', value: 'ABA' },
                { label: 'Artist/Writer', value: 'ABL' },
                { label: 'Composer/Director/Producer', value: 'ABM' },
                { label: 'Designer/Photographer', value: 'ABN' },
                { label: 'Performer', value: 'ABP' },
                { label: 'Supervisor/Manager', value: 'ABQ' },
                { label: 'Other - Art/Design/Media', value: 'ABZ' }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                { label: 'Analyst', value: 'AC2' },
                { label: 'Appraiser - Real Estate', value: 'AC3' },
                { label: 'Broker', value: 'AC5' },
                { label: 'CSR/Teller', value: 'ACB' },
                { label: 'Manager - Property', value: 'ACL' },
                { label: 'Accountant', value: 'ACS' },
                { label: 'Billing & Collections', value: 'ACT' },
                { label: 'Finance/Insurance/Real Estate Rep', value: 'ACU' },
                { label: 'Financial Advisor/Investor', value: 'ACV' },
                { label: 'Loan Officer', value: 'ACW' },
                { label: 'Sales/Purchasing Rep', value: 'ACX' },
                { label: 'Supervisor/Manager', value: 'ACY' },
                { label: 'Other - Banking/Finance/Real Estate', value: 'ACZ' }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                { label: 'Account Executive', value: 'AD0' },
                { label: 'Administrative Assistant', value: 'AD1' },
                { label: 'Cashier/Checker', value: 'AD3' },
                { label: 'Consultant', value: 'AD5' },
                { label: 'Customer Service Representative', value: 'AD6' },
                { label: 'Accountant/Auditor', value: 'ADR' },
                { label: 'Agent/Broker', value: 'ADS' },
                { label: 'Bookkeeper', value: 'ADV' },
                { label: 'General Office Support', value: 'ADW' },
                { label: 'Human Resource (HR) Specialist', value: 'ADX' },
                { label: 'Manager - Executive/GM', value: 'BD0' },
                { label: 'Manager - Other than Executive/GM', value: 'BD1' },
                { label: 'Manufacturing Rep', value: 'BD2' },
                { label: 'Marketing Specialist', value: 'BD3' },
                { label: 'Receptionist', value: 'BD7' },
                { label: 'Retail Salesperson', value: 'BD8' },
                { label: 'Supervisor/Manager (Retail)', value: 'BDA' },
                { label: 'Supervisor/Manager (Non - Retail)', value: 'BDB' },
                { label: 'Vehicle/Equip Rental Rep', value: 'BDC' },
                { label: 'Other - Sales', value: 'BDZ' },
                { label: 'Other - Business/Office', value: 'CDZ' }
              ]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [
                { label: 'Electrician/Linesman', value: 'AEA' },
                { label: 'Machine/Equipment Operator', value: 'AEC' },
                { label: 'HVAC Technician', value: 'AEG' },
                { label: 'Laborer/Worker', value: 'AEJ' },
                { label: 'Plumber', value: 'AEQ' },
                { label: 'Field Service Technician', value: 'AEU' },
                { label: 'Foreman/Manager/Inspector', value: 'AEV' },
                { label: 'Maintenance/Custodian', value: 'AEW' },
                { label: 'Other - Construction/Energy/Mining', value: 'AEZ' }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                { label: 'Counselor', value: 'AF4' },
                { label: 'Librarian/Curator', value: 'AF7' },
                { label: 'Principal/Administrator', value: 'AF8' },
                { label: 'Teacher, K - 12', value: 'AFD' },
                { label: 'Assistant/Aide', value: 'AFG' },
                { label: 'College Professor', value: 'AFH' },
                { label: 'Teacher - Vocational', value: 'AFK' },
                { label: 'Other - Education/Library', value: 'AFZ' }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer', value: 'AG8' },
                { label: 'Technician', value: 'AGK' },
                { label: 'Analyst - Engineering', value: 'AGL' },
                { label: 'Architect/Drafter', value: 'AGN' },
                { label: 'Scientist/Researcher', value: 'AGP' },
                { label: 'Supervisor/Manager', value: 'AGQ' },
                { label: 'Other - Engineer/Architect', value: 'AGZ' }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [
                { label: 'Bartender', value: 'AH1' },
                { label: 'Caterer', value: 'AH4' },
                { label: 'Chef', value: 'AH5' },
                { label: 'Cook/Worker - Fast Food', value: 'AH9' },
                { label: 'Desk Clerk', value: 'AHB' },
                { label: 'Dishwasher', value: 'AHC' },
                { label: 'Housekeeper/Maid', value: 'AHF' },
                { label: 'Food Preparer/Server', value: 'AHL' },
                { label: 'Manager', value: 'AHM' },
                { label: 'Other - Hotel Services', value: 'AHR' },
                { label: 'Other - Food Services', value: 'AHS' }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                { label: 'Military - Enlisted (E1 - E4)', value: 'AJ9' },
                { label: 'Military - Warrant Officer (W1 - W5)', value: 'AJF' },
                { label: 'US Postal Worker', value: 'AJM' },
                { label: 'Auditor/Analyst/Examiner', value: 'AJN' },
                { label: 'Military - Officer (E5 - O10)', value: 'AJP' },
                { label: 'Supervisor/Manager', value: 'AJQ' },
                { label: 'Other - Government/Military', value: 'AJZ' }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                { label: 'Analyst', value: 'AK1' },
                { label: 'Director/Administrator', value: 'AK3' },
                { label: 'Computer Programmer', value: 'AKA' },
                { label: 'Supervisor/Manager', value: 'AKH' },
                { label: 'Technician', value: 'AKJ' },
                { label: 'Other - Information Technology', value: 'AKZ' }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                { label: 'Agent/Broker', value: 'AL4' },
                { label: 'Claims Adjuster/Investigator', value: 'AL7' },
                { label: 'Supervisor/Manager', value: 'ALJ' },
                { label: 'Underwriter/Actuary', value: 'ALK' },
                { label: 'Other - Insurance', value: 'ALZ' }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Correctional or Probation Officer', value: 'AM4' },
                { label: 'Legal Support Staff', value: 'AMG' },
                { label: 'Security Guard', value: 'AMN' },
                { label: 'Lawyer/Judge/Mediator', value: 'AMQ' },
                { label: 'Fire Fighter', value: 'AMR' },
                { label: 'Police/Highway Patrol/Sheriff', value: 'AMS' },
                { label: 'Detective/Investigator', value: 'AMT' },
                { label: 'Supervisor/Manager', value: 'AMU' },
                { label: 'Other - Legal/Law Enf/Security', value: 'AMZ' }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Chiropractor', value: 'AN2' },
                { label: 'Clergy', value: 'AN3' },
                { label: 'Licensed Nurse (LPN)', value: 'ANE' },
                { label: 'Registered Nurse (RN)', value: 'ANF' },
                { label: 'Paramedic/E.M.T.', value: 'ANK' },
                { label: 'Pharmacist', value: 'ANL' },
                { label: 'Social Worker', value: 'ANQ' },
                { label: 'Veterinarian', value: 'ANU' },
                { label: 'Aide/Attendant', value: 'ANV' },
                { label: 'Assistant/Technician/Therapist', value: 'ANW' },
                { label: 'Dentist/Orthodontist', value: 'ANY' },
                { label: 'Doctor/Surgeon', value: 'BN0' },
                { label: 'Psychologist/Therapist', value: 'BN1' },
                {
                  label: 'Other - Medical/Social Services/Religion',
                  value: 'BNZ'
                }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [
                { label: 'Child or Elderly Care Provider', value: 'AP0' },
                { label: 'Dry Cleaning/Laundry Servicer', value: 'AP1' },
                { label: 'Hair/Personal Care Provider', value: 'AP2' },
                { label: 'Cleaner/Housekeeper', value: 'AP3' },
                { label: 'Pet Service Provider', value: 'AP7' },
                { label: 'Other - Personal Care/Service', value: 'APZ' }
              ]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [
                { label: 'Machine Operator', value: 'AQ7' },
                { label: 'Assembler/Fabricator', value: 'AQJ' },
                { label: 'Inspector/Quality Control', value: 'AQK' },
                { label: 'Supervisor/Manager', value: 'AQP' },
                { label: 'Other - Production/Manufacturing', value: 'AQZ' }
              ]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [
                { label: 'Custodian/Janitor', value: 'AR1' },
                { label: 'Landscape/Grounds Maintenance', value: 'AR7' },
                { label: 'Laborer/Helper', value: 'ARA' },
                { label: 'Repair/Mechanic', value: 'ARB' },
                { label: 'Other - Repair/Maintenance/Grounds', value: 'ARZ' }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                { label: 'Athlete', value: 'AS3' },
                { label: 'Admin/Activity Assistant', value: 'ASJ' },
                { label: 'Fitness Trainer/Instructor', value: 'ASK' },
                { label: 'Supervisor/Manager', value: 'ASL' },
                { label: 'Other - Sports/Recreation', value: 'ASZ' }
              ]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Driver (All Vehicles)', value: 'ATQ' },
                { label: 'Packer/Mover', value: 'ATR' },
                { label: 'Supervisor/Manager', value: 'ATS' },
                { label: 'Other - Travel/Transportation/Storage', value: 'ATZ' }
              ]
            },
            {
              industry: 'Other',
              occupations: []
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ]
        },
        CO: {
          recentCoverageLimits: [
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ],
          occupation: [
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [{ label: 'Agriculture/Forestry/Fishing', value: 9 }]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [{ label: 'Art/Design/Media', value: 19 }]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [{ label: 'Banking/Finance/Real Estate', value: 10 }]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [{ label: 'Business/Sales/Office', value: 11 }]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [{ label: 'Construction/Energy/Mining', value: 6 }]
            },
            {
              industry: 'Education/Library',
              occupations: [{ label: 'Education/Library', value: 20 }]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer/Architect/Science/Math', value: 21 }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [{ label: 'Food Service/Hotel Services', value: 5 }]
            },
            {
              industry: 'Government/Military/Veteran',
              occupations: [{ label: 'Government/Military/Veteran', value: 22 }]
            },
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: 3 }]
            },
            {
              industry: 'Information Technology',
              occupations: [{ label: 'Information Technology', value: 12 }]
            },
            {
              industry: 'Insurance',
              occupations: [{ label: 'Insurance', value: 13 }]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Legal/Law Enforcement/Security', value: 14 }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Medical/Social Services/Religion', value: 15 }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [{ label: 'Personal Care/Service', value: 7 }]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [{ label: 'Production/Manufacturing', value: 23 }]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [{ label: 'Repair/Maintenance/Grounds', value: 16 }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: 4 }]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [{ label: 'Sports/Recreation', value: 17 }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: 8 }]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Travel/Transportation/Storage', value: 18 }
              ]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: 2 }]
            }
          ]
        },
        FL: {
          recentCoverageLimits: [
            {
              label: '$10,000/$20,000',
              value: '10/20'
            },
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '$250,000/$500,000 or more',
              value: '>=250/500'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ],
          occupation: [
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                {
                  label: 'EquipmentOperator',
                  value: 1
                },
                {
                  label: 'Farm/RanchOwner',
                  value: 2
                },
                {
                  label: 'Farm/RanchWorker',
                  value: 3
                },
                {
                  label: 'Fisherman',
                  value: 4
                },
                {
                  label: 'Florist',
                  value: 5
                },
                {
                  label: 'Laborer/Worker',
                  value: 6
                },
                {
                  label: 'Landscaper',
                  value: 7
                },
                {
                  label: 'NurseryWorker',
                  value: 8
                },
                {
                  label: 'Logger',
                  value: 9
                },
                {
                  label: 'MillWorker',
                  value: 10
                },
                {
                  label: 'Other',
                  value: 11
                },
                {
                  label: 'Ranger',
                  value: 12
                },
                {
                  label: 'Supervisor',
                  value: 13
                },
                {
                  label: 'TimberGrader/Scaler',
                  value: 14
                }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                {
                  label: 'Actor',
                  value: 15
                },
                {
                  label: 'AdministrativeAssistant',
                  value: 16
                },
                {
                  label: 'Announcer/Broadcaster',
                  value: 17
                },
                {
                  label: 'Artist/Animator',
                  value: 18
                },
                {
                  label: 'Author/Writer',
                  value: 19
                },
                {
                  label: 'Choreography/Dancer',
                  value: 20
                },
                {
                  label: 'Clerk',
                  value: 21
                },
                {
                  label: 'Composer/Director',
                  value: 22
                },
                {
                  label: 'Curator',
                  value: 23
                },
                {
                  label: 'Designer',
                  value: 24
                },
                {
                  label: 'Editor',
                  value: 25
                },
                {
                  label: 'Journalist/Reporter',
                  value: 26
                },
                {
                  label: 'Musician/Singer',
                  value: 27
                },
                {
                  label: 'Other',
                  value: 28
                },
                {
                  label: 'Photographer',
                  value: 29
                },
                {
                  label: 'Printer',
                  value: 30
                },
                {
                  label: 'Producer',
                  value: 31
                },
                {
                  label: 'ProductionCrew',
                  value: 32
                },
                {
                  label: 'Projectionist',
                  value: 33
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 34
                },
                {
                  label: 'TicketSales/Usher',
                  value: 35
                }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                {
                  label: 'Accountant/Auditor',
                  value: 36
                },
                {
                  label: 'AdministrativeAssistant',
                  value: 37
                },
                {
                  label: 'Analyst/Broker',
                  value: 38
                },
                {
                  label: 'Appraiser',
                  value: 39
                },
                {
                  label: 'Bookkeeper',
                  value: 40
                },
                {
                  label: 'BranchManager',
                  value: 41
                },
                {
                  label: 'Clerk',
                  value: 42
                },
                {
                  label: 'Collections',
                  value: 43
                },
                {
                  label: 'Consultant',
                  value: 44
                },
                {
                  label: 'Controller',
                  value: 45
                },
                {
                  label: 'CSR/Teller',
                  value: 46
                },
                {
                  label: 'Director/Administrator',
                  value: 47
                },
                {
                  label: 'Executive',
                  value: 48
                },
                {
                  label: 'FinancialAdvisor',
                  value: 49
                },
                {
                  label: 'InvestmentBanker',
                  value: 50
                },
                {
                  label: 'Investor',
                  value: 51
                },
                {
                  label: 'Loan/EscrowProcessor',
                  value: 52
                },
                {
                  label: 'Manager-Credit/Loan',
                  value: 53
                },
                {
                  label: 'Manager-Portfolio/Production',
                  value: 54
                },
                {
                  label: 'Manager-Property',
                  value: 55
                },
                {
                  label: 'Other',
                  value: 56
                },
                {
                  label: 'Realtor',
                  value: 57
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 58
                },
                {
                  label: 'SalesAgent/Representative',
                  value: 59
                },
                {
                  label: 'Trader,FinancialInstruments',
                  value: 60
                },
                {
                  label: 'Underwriter',
                  value: 61
                }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                {
                  label: 'AccountExecutive',
                  value: 62
                },
                {
                  label: 'AdministrativeAssistant',
                  value: 63
                },
                {
                  label: 'Buyer',
                  value: 64
                },
                {
                  label: 'Cashier/Checker',
                  value: 65
                },
                {
                  label: 'Clerk-Office',
                  value: 66
                },
                {
                  label: 'Consultant',
                  value: 67
                },
                {
                  label: 'CustomerServiceRepresentative',
                  value: 68
                },
                {
                  label: 'Director/Administrator',
                  value: 69
                },
                {
                  label: 'Executive',
                  value: 70
                },
                {
                  label: 'HRRepresentative',
                  value: 71
                },
                {
                  label: 'Manager-DepartmentStore',
                  value: 72
                },
                {
                  label: 'Manager-District',
                  value: 73
                },
                {
                  label: 'Manager-Finance',
                  value: 74
                },
                {
                  label: 'Manager-GeneralOperations',
                  value: 75
                },
                {
                  label: 'Manager-HR/PublicRelations',
                  value: 76
                },
                {
                  label: 'Manager-Marketing/Sales',
                  value: 77
                },
                {
                  label: 'Manager/Supervisor-Office',
                  value: 78
                },
                {
                  label: 'MarketingResearcher',
                  value: 79
                },
                {
                  label: 'Messenger/Courier',
                  value: 80
                },
                {
                  label: 'Other',
                  value: 81
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 82
                },
                {
                  label: 'Sales-Counter/Rental',
                  value: 83
                },
                {
                  label: 'Sales-HomeBased',
                  value: 84
                },
                {
                  label: 'Sales-ManufactureRep',
                  value: 85
                },
                {
                  label: 'Sales-Retail/Wholesale',
                  value: 86
                },
                {
                  label: 'Sales-Route/Vendor',
                  value: 87
                }
              ]
            },
            {
              industry: 'Clerk',
              occupations: [
                {
                  label: 'Clerk',
                  value: 88
                }
              ]
            },
            {
              industry: 'Construction/Energy Trades',
              occupations: [
                {
                  label: 'Appraiser',
                  value: 89
                },
                {
                  label: 'BoilerOperator/Maker',
                  value: 90
                },
                {
                  label: 'Bricklayer/Mason',
                  value: 91
                },
                {
                  label: 'Carpenter',
                  value: 92
                },
                {
                  label: 'CarpetInstaller',
                  value: 93
                },
                {
                  label: 'ConcreteWorker',
                  value: 94
                },
                {
                  label: 'Contractor',
                  value: 95
                },
                {
                  label: 'CraneOperator',
                  value: 96
                },
                {
                  label: 'Electrician/Linesman',
                  value: 97
                },
                {
                  label: 'ElevatorTechnician/Installer',
                  value: 98
                },
                {
                  label: 'EquipmentOperator',
                  value: 99
                },
                {
                  label: 'FloorLayer/Finisher',
                  value: 100
                },
                {
                  label: 'Handyman',
                  value: 101
                },
                {
                  label: 'Heat/AirTechnician',
                  value: 102
                },
                {
                  label: 'Inspector',
                  value: 103
                },
                {
                  label: 'Laborer/Worker',
                  value: 104
                },
                {
                  label: 'Metalworker',
                  value: 105
                },
                {
                  label: 'Miner',
                  value: 106
                },
                {
                  label: 'Oil/GasDriller/RigOperator',
                  value: 107
                },
                {
                  label: 'Other',
                  value: 108
                },
                {
                  label: 'Painter',
                  value: 109
                },
                {
                  label: 'Plaster/Drywall/Stucco',
                  value: 110
                },
                {
                  label: 'Plumber',
                  value: 111
                },
                {
                  label: 'ProjectMgr',
                  value: 112
                },
                {
                  label: 'Roofer',
                  value: 113
                },
                {
                  label: 'UtilityWorker',
                  value: 114
                },
                {
                  label: 'Welder',
                  value: 115
                }
              ]
            },
            {
              industry: 'Conversion',
              occupations: [
                {
                  label: 'Conversion',
                  value: 116
                }
              ]
            },
            {
              industry: 'Disabled',
              occupations: [
                {
                  label: 'Disabled',
                  value: 117
                }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                {
                  label: 'AdministrativeAssistant',
                  value: 118
                },
                {
                  label: 'Audio-VisualTech',
                  value: 119
                },
                {
                  label: 'Child/DaycareWorker',
                  value: 120
                },
                {
                  label: 'Clerk',
                  value: 121
                },
                {
                  label: 'Counselor',
                  value: 122
                },
                {
                  label: 'GraduateTeachingAssistant',
                  value: 123
                },
                {
                  label: 'Instructor-Vocation',
                  value: 124
                },
                {
                  label: 'Librarian/Curator',
                  value: 125
                },
                {
                  label: 'Other',
                  value: 126
                },
                {
                  label: 'Principal',
                  value: 127
                },
                {
                  label: 'Professor,College',
                  value: 128
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 129
                },
                {
                  label: 'Superintendent',
                  value: 130
                },
                {
                  label: 'Teacher,College',
                  value: 131
                },
                {
                  label: 'Teacher,K-12',
                  value: 132
                },
                {
                  label: 'TeachingAssistant/Aide',
                  value: 133
                },
                {
                  label: 'Tutor',
                  value: 134
                }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                {
                  label: 'Actuary',
                  value: 135
                },
                {
                  label: 'AdministrativeAssistant',
                  value: 136
                },
                {
                  label: 'Analyst',
                  value: 137
                },
                {
                  label: 'Architect',
                  value: 138
                },
                {
                  label: 'Chemist',
                  value: 139
                },
                {
                  label: 'Clerk',
                  value: 140
                },
                {
                  label: 'ClinicalDataCoordinator',
                  value: 141
                },
                {
                  label: 'Drafter',
                  value: 142
                },
                {
                  label: 'Engineer',
                  value: 143
                },
                {
                  label: 'LabAssistant',
                  value: 144
                },
                {
                  label: 'Manager-Project',
                  value: 145
                },
                {
                  label: 'Manager-R&D',
                  value: 146
                },
                {
                  label: 'Mathematician',
                  value: 147
                },
                {
                  label: 'Other',
                  value: 148
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 149
                },
                {
                  label: 'ResearchProgramDirector',
                  value: 150
                },
                {
                  label: 'Researcher',
                  value: 151
                },
                {
                  label: 'Scientist',
                  value: 152
                },
                {
                  label: 'Sociologist',
                  value: 153
                },
                {
                  label: 'Surveyor/Mapmaker',
                  value: 154
                },
                {
                  label: 'Technician',
                  value: 155
                }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                {
                  label: 'Accountant/Auditor',
                  value: 156
                },
                {
                  label: 'AdministrativeAssistant',
                  value: 157
                },
                {
                  label: 'Analyst',
                  value: 158
                },
                {
                  label: 'Attorney',
                  value: 159
                },
                {
                  label: 'ChiefExecutive',
                  value: 160
                },
                {
                  label: 'Clerk',
                  value: 161
                },
                {
                  label: 'Commissioner',
                  value: 162
                },
                {
                  label: 'CouncilMember',
                  value: 163
                },
                {
                  label: 'Director/Administrator',
                  value: 164
                },
                {
                  label: 'EnlistedMilitaryPersonnel(E1-4)',
                  value: 165
                },
                {
                  label: 'Legislator',
                  value: 166
                },
                {
                  label: 'Mayor/CityManager',
                  value: 167
                },
                {
                  label: 'MeterReader',
                  value: 168
                },
                {
                  label: 'NCO(E5-9)',
                  value: 169
                },
                {
                  label: 'Officer-Commissioned',
                  value: 170
                },
                {
                  label: 'Officer-Warrant',
                  value: 171
                },
                {
                  label: 'Other',
                  value: 172
                },
                {
                  label: 'ParkRanger',
                  value: 173
                },
                {
                  label: 'Planner',
                  value: 174
                },
                {
                  label: 'Postmaster',
                  value: 175
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 176
                },
                {
                  label: 'Regulator',
                  value: 177
                },
                {
                  label: 'USPostalWorker',
                  value: 178
                }
              ]
            },
            {
              industry: 'Homemaker/Houseperson',
              occupations: [
                {
                  label: 'Homemaker/Houseperson',
                  value: 179
                }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                {
                  label: 'AdministrativeAssistant',
                  value: 180
                },
                {
                  label: 'Analyst',
                  value: 181
                },
                {
                  label: 'Clerk',
                  value: 182
                },
                {
                  label: 'Engineer-Hardware',
                  value: 183
                },
                {
                  label: 'Engineer-Software',
                  value: 184
                },
                {
                  label: 'Engineer-Systems',
                  value: 185
                },
                {
                  label: 'Executive',
                  value: 186
                },
                {
                  label: 'Manager-Systems',
                  value: 187
                },
                {
                  label: 'NetworkAdministrator',
                  value: 188
                },
                {
                  label: 'Other',
                  value: 189
                },
                {
                  label: 'Programmer',
                  value: 190
                },
                {
                  label: 'ProjectCoordinator',
                  value: 191
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 192
                },
                {
                  label: 'SupportTechnician',
                  value: 193
                },
                {
                  label: 'SystemsSecurity',
                  value: 194
                },
                {
                  label: 'TechnicalWriter',
                  value: 195
                },
                {
                  label: 'WebDeveloper',
                  value: 196
                }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                {
                  label: 'Accountant/Auditor',
                  value: 197
                },
                {
                  label: 'Actuary',
                  value: 198
                },
                {
                  label: 'AdministrativeAssistant',
                  value: 199
                },
                {
                  label: 'Agent/Broker',
                  value: 200
                },
                {
                  label: 'Analyst',
                  value: 201
                },
                {
                  label: 'Attorney',
                  value: 202
                },
                {
                  label: 'ClaimsAdjuster',
                  value: 203
                },
                {
                  label: 'Clerk',
                  value: 204
                },
                {
                  label: 'Commissioner',
                  value: 205
                },
                {
                  label: 'CustomerServiceRepresentative',
                  value: 206
                },
                {
                  label: 'Director/Administrator',
                  value: 207
                },
                {
                  label: 'Executive',
                  value: 208
                },
                {
                  label: 'InsuranceCSR',
                  value: 209
                },
                {
                  label: 'Other',
                  value: 210
                },
                {
                  label: 'ProductManager',
                  value: 211
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 212
                },
                {
                  label: 'SalesRepresentative',
                  value: 213
                },
                {
                  label: 'Underwriter',
                  value: 214
                }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                {
                  label: 'AirportSecurityOfficer',
                  value: 215
                },
                {
                  label: 'AnimalControlOfficer',
                  value: 216
                },
                {
                  label: 'Attorney',
                  value: 217
                },
                {
                  label: 'Bailiff',
                  value: 218
                },
                {
                  label: 'CorrectionOfficer',
                  value: 219
                },
                {
                  label: 'CourtClerk/Reporter',
                  value: 220
                },
                {
                  label: 'DeputySheriff',
                  value: 221
                },
                {
                  label: 'Dispatcher',
                  value: 222
                },
                {
                  label: 'Examiner',
                  value: 223
                },
                {
                  label: 'FederalAgent/Marshall',
                  value: 224
                },
                {
                  label: 'FireChief',
                  value: 225
                },
                {
                  label: 'FireFighter/Supervisor',
                  value: 226
                },
                {
                  label: 'GamingOfficer/Investigator',
                  value: 227
                },
                {
                  label: 'HighwayPatrolOfficer',
                  value: 228
                },
                {
                  label: 'Judge/HearingOfficer',
                  value: 229
                },
                {
                  label: 'LegalAssistant/Secretary',
                  value: 230
                },
                {
                  label: 'Other',
                  value: 231
                },
                {
                  label: 'Paralegal/LawClerk',
                  value: 232
                },
                {
                  label: 'PoliceChief',
                  value: 233
                },
                {
                  label: 'PoliceDetective/Investigator',
                  value: 234
                },
                {
                  label: 'PoliceOfficer/Supervisor',
                  value: 235
                },
                {
                  label: 'PrivateInvestigator/Detective',
                  value: 236
                },
                {
                  label: 'ProcessServer',
                  value: 237
                },
                {
                  label: 'SecurityGuard',
                  value: 238
                },
                {
                  label: 'Sheriff',
                  value: 239
                }
              ]
            },
            {
              industry: 'Maintenance/Repair/Housekeeping',
              occupations: [
                {
                  label: 'BuildingMaintenanceEngineer',
                  value: 240
                },
                {
                  label: 'Custodian/Janitor',
                  value: 241
                },
                {
                  label: 'Electrician',
                  value: 242
                },
                {
                  label: 'FieldServiceTechnician',
                  value: 243
                },
                {
                  label: 'Handyman',
                  value: 244
                },
                {
                  label: 'Heat/AirConditionerRepairman',
                  value: 245
                },
                {
                  label: 'Housekeeper/Maid',
                  value: 246
                },
                {
                  label: 'Landscape/GroundsMaintenance',
                  value: 247
                },
                {
                  label: 'MaintenanceMechanic',
                  value: 248
                },
                {
                  label: 'Mechanic',
                  value: 249
                },
                {
                  label: 'Other',
                  value: 250
                }
              ]
            },
            {
              industry: 'Manufacturing/Production',
              occupations: [
                {
                  label: 'AdministrativeAssistant',
                  value: 251
                },
                {
                  label: 'Clerk',
                  value: 252
                },
                {
                  label: 'FactoryWorker',
                  value: 253
                },
                {
                  label: 'Foreman/Supervisor',
                  value: 254
                },
                {
                  label: 'FurnitureFinisher',
                  value: 255
                },
                {
                  label: 'Inspector',
                  value: 256
                },
                {
                  label: 'Jeweler',
                  value: 257
                },
                {
                  label: 'MachineOperator',
                  value: 258
                },
                {
                  label: 'Other',
                  value: 259
                },
                {
                  label: 'Packer',
                  value: 260
                },
                {
                  label: 'PlantManager',
                  value: 261
                },
                {
                  label: 'Printer/Bookbinder',
                  value: 262
                },
                {
                  label: 'QualityControl',
                  value: 263
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 264
                },
                {
                  label: 'RefiningOperator',
                  value: 265
                },
                {
                  label: 'Shoemaker',
                  value: 266
                },
                {
                  label: 'Tailor/CustomSewer',
                  value: 267
                },
                {
                  label: 'TextileWorker',
                  value: 268
                },
                {
                  label: 'Upholsterer',
                  value: 269
                }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                {
                  label: 'AdministrativeAssistant',
                  value: 270
                },
                {
                  label: 'Assistant-Medic/Dent/Vet',
                  value: 271
                },
                {
                  label: 'Chiropractor',
                  value: 272
                },
                {
                  label: 'Clergy',
                  value: 273
                },
                {
                  label: 'Clerk',
                  value: 274
                },
                {
                  label: 'ClientCareWorker',
                  value: 275
                },
                {
                  label: 'DentalHygienist',
                  value: 276
                },
                {
                  label: 'Dentist',
                  value: 277
                },
                {
                  label: 'Dietician',
                  value: 278
                },
                {
                  label: 'Doctor',
                  value: 279
                },
                {
                  label: 'HospiceVolunteer',
                  value: 280
                },
                {
                  label: 'LabAssistant',
                  value: 281
                },
                {
                  label: 'Mortician',
                  value: 282
                },
                {
                  label: 'Nurse-C.N.A.',
                  value: 283
                },
                {
                  label: 'Nurse-LPN',
                  value: 284
                },
                {
                  label: 'Nurse-RN',
                  value: 285
                },
                {
                  label: 'NursePractitioner',
                  value: 286
                },
                {
                  label: 'Optometrist',
                  value: 287
                },
                {
                  label: 'Orthodontist',
                  value: 288
                },
                {
                  label: 'Other',
                  value: 289
                },
                {
                  label: 'Paramedic/E.M.Technician',
                  value: 290
                },
                {
                  label: 'Pharmacist',
                  value: 291
                },
                {
                  label: 'PhysicalTherapist',
                  value: 292
                },
                {
                  label: 'Psychologist',
                  value: 293
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 294
                },
                {
                  label: 'SocialWorker',
                  value: 295
                },
                {
                  label: 'SupportServices',
                  value: 296
                },
                {
                  label: 'Technician',
                  value: 297
                },
                {
                  label: 'Therapist',
                  value: 298
                },
                {
                  label: 'Veterinarian',
                  value: 299
                }
              ]
            },
            {
              industry: 'Other',
              occupations: [
                {
                  label: 'Other',
                  value: 300
                }
              ]
            },
            {
              industry: 'Personal',
              occupations: [
                {
                  label: 'Care/ServiceCaregiver',
                  value: 301
                },
                {
                  label: 'Care/ServiceDryCleaner/Laundry',
                  value: 302
                },
                {
                  label: 'Care/ServiceHairStylist/Barber',
                  value: 303
                },
                {
                  label: 'Care/ServiceHousekeeper',
                  value: 304
                },
                {
                  label: 'Care/ServiceManicurist',
                  value: 305
                },
                {
                  label: 'Care/ServiceMasseuse',
                  value: 306
                },
                {
                  label: 'Care/ServiceNanny',
                  value: 307
                },
                {
                  label: 'Care/ServiceOther',
                  value: 308
                },
                {
                  label: 'Care/ServicePetServices',
                  value: 309
                },
                {
                  label: 'Care/ServiceReceptionist/Secretary',
                  value: 310
                }
              ]
            },
            {
              industry: 'Restaurant/Hotel Services',
              occupations: [
                {
                  label: 'Baker',
                  value: 311
                },
                {
                  label: 'Bartender',
                  value: 312
                },
                {
                  label: 'Bellhop',
                  value: 313
                },
                {
                  label: 'BusPerson',
                  value: 314
                },
                {
                  label: 'Caterer',
                  value: 315
                },
                {
                  label: 'Chef',
                  value: 316
                },
                {
                  label: 'Concessionaire',
                  value: 317
                },
                {
                  label: 'Concierge',
                  value: 318
                },
                {
                  label: 'Cook-Restaurant/Cafeteria',
                  value: 319
                },
                {
                  label: 'Cook/Worker-FastFood',
                  value: 320
                },
                {
                  label: 'DeliveryPerson',
                  value: 321
                },
                {
                  label: 'DeskClerk',
                  value: 322
                },
                {
                  label: 'Dishwasher',
                  value: 323
                },
                {
                  label: 'FoodProduction/Packing',
                  value: 324
                },
                {
                  label: 'Host/Maitred',
                  value: 325
                },
                {
                  label: 'Housekeeper/Maid',
                  value: 326
                },
                {
                  label: 'Manager',
                  value: 327
                },
                {
                  label: 'Other',
                  value: 328
                },
                {
                  label: 'Valet',
                  value: 329
                },
                {
                  label: 'Waiter/Waitress',
                  value: 330
                },
                {
                  label: 'WineSteward',
                  value: 331
                }
              ]
            },
            {
              industry: 'Retired 55 and above',
              occupations: [
                {
                  label: 'Retired',
                  value: 332
                }
              ]
            },
            {
              industry: 'Retired less than 55',
              occupations: [
                {
                  label: 'Retired',
                  value: 333
                }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                {
                  label: 'Activity/RecreationalAssistant',
                  value: 334
                },
                {
                  label: 'AdministrativeAssistant',
                  value: 335
                },
                {
                  label: 'Agent',
                  value: 336
                },
                {
                  label: 'Athlete',
                  value: 337
                },
                {
                  label: 'Clerk',
                  value: 338
                },
                {
                  label: 'Coach',
                  value: 339
                },
                {
                  label: 'Concessionaire',
                  value: 340
                },
                {
                  label: 'Director,Program',
                  value: 341
                },
                {
                  label: 'EventManager/Promoter',
                  value: 342
                },
                {
                  label: 'LifeGuard',
                  value: 343
                },
                {
                  label: 'Manager-FitnessClub',
                  value: 344
                },
                {
                  label: 'Other',
                  value: 345
                },
                {
                  label: 'ParkRanger',
                  value: 346
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 347
                },
                {
                  label: 'Sales-Ticket/Membership',
                  value: 348
                },
                {
                  label: 'Sports-Broadcaster/Journalist',
                  value: 349
                },
                {
                  label: 'Trainer/Instructor',
                  value: 350
                },
                {
                  label: 'Umpire/Referee',
                  value: 351
                }
              ]
            },
            {
              industry: 'Student',
              occupations: [
                {
                  label: 'GraduateStudent',
                  value: 352
                },
                {
                  label: 'HighSchool',
                  value: 353
                },
                {
                  label: 'Other',
                  value: 354
                },
                {
                  label: 'Undergraduate',
                  value: 355
                }
              ]
            },
            {
              industry: 'Travel/Transportation/Warehousing',
              occupations: [
                {
                  label: 'AdministrativeAssistant',
                  value: 356
                },
                {
                  label: 'AirTrafficControl',
                  value: 357
                },
                {
                  label: 'AirportOperationCrew',
                  value: 358
                },
                {
                  label: 'Bellhop/Porter',
                  value: 359
                },
                {
                  label: 'Clerk',
                  value: 360
                },
                {
                  label: 'CraneLoader/Operator',
                  value: 361
                },
                {
                  label: 'Dispatcher',
                  value: 362
                },
                {
                  label: 'Driver-Bus/Streetcar',
                  value: 363
                },
                {
                  label: 'Driver-Taxi/Limo',
                  value: 364
                },
                {
                  label: 'Driver-Truck/Delivery',
                  value: 365
                },
                {
                  label: 'FlightAttendant',
                  value: 366
                },
                {
                  label: 'ForkliftOperator',
                  value: 367
                },
                {
                  label: 'Laborer',
                  value: 368
                },
                {
                  label: 'Longshoreman',
                  value: 369
                },
                {
                  label: 'Manager-Warehouse/District',
                  value: 370
                },
                {
                  label: 'Mate/Sailor',
                  value: 371
                },
                {
                  label: 'Other',
                  value: 372
                },
                {
                  label: 'ParkingLotAttendant',
                  value: 373
                },
                {
                  label: 'Pilot/Captain/Engineer',
                  value: 374
                },
                {
                  label: 'RailroadWorker',
                  value: 375
                },
                {
                  label: 'Receptionist/Secretary',
                  value: 376
                },
                {
                  label: 'Shipping/ReceivingClerk',
                  value: 377
                },
                {
                  label: 'Subway/LightRailOperator',
                  value: 378
                },
                {
                  label: 'TicketAgent',
                  value: 379
                },
                {
                  label: 'TransportationSpecialist',
                  value: 380
                },
                {
                  label: 'TravelAgent',
                  value: 381
                }
              ]
            },
            {
              industry: 'Unemployed',
              occupations: [
                {
                  label: 'Unemployed',
                  value: 382
                }
              ]
            }
          ]
        },
        IL: {
          recentCoverageLimits: [
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ]
        },
        OH: {
          recentCoverageLimits: [
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          occupation: [
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: '10' }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: '20' }]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: '30' }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: '40' }]
            },
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                { label: 'Farm/Ranch Owner', value: 'AA4' },
                { label: 'Laborer/Worker', value: 'AA8' },
                { label: 'Supervisor/Manager', value: 'AAE' },
                { label: 'Inspector/Quality Control', value: 'AAG' },
                { label: 'Other - Agriculture/Forestry/Fishing', value: 'AAZ' }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                { label: 'Editor', value: 'ABA' },
                { label: 'Artist/Writer', value: 'ABL' },
                { label: 'Composer/Director/Producer', value: 'ABM' },
                { label: 'Designer/Photographer', value: 'ABN' },
                { label: 'Performer', value: 'ABP' },
                { label: 'Supervisor/Manager', value: 'ABQ' },
                { label: 'Other - Art/Design/Media', value: 'ABZ' }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                { label: 'Analyst', value: 'AC2' },
                { label: 'Appraiser - Real Estate', value: 'AC3' },
                { label: 'Broker', value: 'AC5' },
                { label: 'CSR/Teller', value: 'ACB' },
                { label: 'Manager - Property', value: 'ACL' },
                { label: 'Accountant', value: 'ACS' },
                { label: 'Billing & Collections', value: 'ACT' },
                { label: 'Finance/Insurance/Real Estate Rep', value: 'ACU' },
                { label: 'Financial Advisor/Investor', value: 'ACV' },
                { label: 'Loan Officer', value: 'ACW' },
                { label: 'Sales/Purchasing Rep', value: 'ACX' },
                { label: 'Supervisor/Manager', value: 'ACY' },
                { label: 'Other - Banking/Finance/Real Estate', value: 'ACZ' }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                { label: 'Account Executive', value: 'AD0' },
                { label: 'Administrative Assistant', value: 'AD1' },
                { label: 'Cashier/Checker', value: 'AD3' },
                { label: 'Consultant', value: 'AD5' },
                { label: 'Customer Service Representative', value: 'AD6' },
                { label: 'Accountant/Auditor', value: 'ADR' },
                { label: 'Agent/Broker', value: 'ADS' },
                { label: 'Bookkeeper', value: 'ADV' },
                { label: 'General Office Support', value: 'ADW' },
                { label: 'Human Resource (HR) Specialist', value: 'ADX' },
                { label: 'Manager - Executive/GM', value: 'BD0' },
                { label: 'Manager - Other than Executive/GM', value: 'BD1' },
                { label: 'Manufacturing Rep', value: 'BD2' },
                { label: 'Marketing Specialist', value: 'BD3' },
                { label: 'Receptionist', value: 'BD7' },
                { label: 'Retail Salesperson', value: 'BD8' },
                { label: 'Supervisor/Manager (Retail)', value: 'BDA' },
                { label: 'Supervisor/Manager (Non - Retail)', value: 'BDB' },
                { label: 'Vehicle/Equip Rental Rep', value: 'BDC' },
                { label: 'Other - Sales', value: 'BDZ' },
                { label: 'Other - Business/Office', value: 'CDZ' }
              ]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [
                { label: 'Electrician/Linesman', value: 'AEA' },
                { label: 'Machine/Equipment Operator', value: 'AEC' },
                { label: 'HVAC Technician', value: 'AEG' },
                { label: 'Laborer/Worker', value: 'AEJ' },
                { label: 'Plumber', value: 'AEQ' },
                { label: 'Field Service Technician', value: 'AEU' },
                { label: 'Foreman/Manager/Inspector', value: 'AEV' },
                { label: 'Maintenance/Custodian', value: 'AEW' },
                { label: 'Other - Construction/Energy/Mining', value: 'AEZ' }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                { label: 'Counselor', value: 'AF4' },
                { label: 'Librarian/Curator', value: 'AF7' },
                { label: 'Principal/Administrator', value: 'AF8' },
                { label: 'Teacher, K - 12', value: 'AFD' },
                { label: 'Assistant/Aide', value: 'AFG' },
                { label: 'College Professor', value: 'AFH' },
                { label: 'Teacher - Vocational', value: 'AFK' },
                { label: 'Other - Education/Library', value: 'AFZ' }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer', value: 'AG8' },
                { label: 'Technician', value: 'AGK' },
                { label: 'Analyst - Engineering', value: 'AGL' },
                { label: 'Architect/Drafter', value: 'AGN' },
                { label: 'Scientist/Researcher', value: 'AGP' },
                { label: 'Supervisor/Manager', value: 'AGQ' },
                { label: 'Other - Engineer/Architect', value: 'AGZ' }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [
                { label: 'Bartender', value: 'AH1' },
                { label: 'Caterer', value: 'AH4' },
                { label: 'Chef', value: 'AH5' },
                { label: 'Cook/Worker - Fast Food', value: 'AH9' },
                { label: 'Desk Clerk', value: 'AHB' },
                { label: 'Dishwasher', value: 'AHC' },
                { label: 'Housekeeper/Maid', value: 'AHF' },
                { label: 'Food Preparer/Server', value: 'AHL' },
                { label: 'Manager', value: 'AHM' },
                { label: 'Other - Hotel Services', value: 'AHR' },
                { label: 'Other - Food Services', value: 'AHS' }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                { label: 'Military - Enlisted (E1 - E4)', value: 'AJ9' },
                { label: 'Military - Warrant Officer (W1 - W5)', value: 'AJF' },
                { label: 'US Postal Worker', value: 'AJM' },
                { label: 'Auditor/Analyst/Examiner', value: 'AJN' },
                { label: 'Military - Officer (E5 - O10)', value: 'AJP' },
                { label: 'Supervisor/Manager', value: 'AJQ' },
                { label: 'Other - Government/Military', value: 'AJZ' }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                { label: 'Analyst', value: 'AK1' },
                { label: 'Director/Administrator', value: 'AK3' },
                { label: 'Computer Programmer', value: 'AKA' },
                { label: 'Supervisor/Manager', value: 'AKH' },
                { label: 'Technician', value: 'AKJ' },
                { label: 'Other - Information Technology', value: 'AKZ' }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                { label: 'Agent/Broker', value: 'AL4' },
                { label: 'Claims Adjuster/Investigator', value: 'AL7' },
                { label: 'Supervisor/Manager', value: 'ALJ' },
                { label: 'Underwriter/Actuary', value: 'ALK' },
                { label: 'Other - Insurance', value: 'ALZ' }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Correctional or Probation Officer', value: 'AM4' },
                { label: 'Legal Support Staff', value: 'AMG' },
                { label: 'Security Guard', value: 'AMN' },
                { label: 'Lawyer/Judge/Mediator', value: 'AMQ' },
                { label: 'Fire Fighter', value: 'AMR' },
                { label: 'Police/Highway Patrol/Sheriff', value: 'AMS' },
                { label: 'Detective/Investigator', value: 'AMT' },
                { label: 'Supervisor/Manager', value: 'AMU' },
                { label: 'Other - Legal/Law Enf/Security', value: 'AMZ' }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Chiropractor', value: 'AN2' },
                { label: 'Clergy', value: 'AN3' },
                { label: 'Licensed Nurse (LPN)', value: 'ANE' },
                { label: 'Registered Nurse (RN)', value: 'ANF' },
                { label: 'Paramedic/E.M.T.', value: 'ANK' },
                { label: 'Pharmacist', value: 'ANL' },
                { label: 'Social Worker', value: 'ANQ' },
                { label: 'Veterinarian', value: 'ANU' },
                { label: 'Aide/Attendant', value: 'ANV' },
                { label: 'Assistant/Technician/Therapist', value: 'ANW' },
                { label: 'Dentist/Orthodontist', value: 'ANY' },
                { label: 'Doctor/Surgeon', value: 'BN0' },
                { label: 'Psychologist/Therapist', value: 'BN1' },
                {
                  label: 'Other - Medical/Social Services/Religion',
                  value: 'BNZ'
                }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [
                { label: 'Child or Elderly Care Provider', value: 'AP0' },
                { label: 'Dry Cleaning/Laundry Servicer', value: 'AP1' },
                { label: 'Hair/Personal Care Provider', value: 'AP2' },
                { label: 'Cleaner/Housekeeper', value: 'AP3' },
                { label: 'Pet Service Provider', value: 'AP7' },
                { label: 'Other - Personal Care/Service', value: 'APZ' }
              ]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [
                { label: 'Machine Operator', value: 'AQ7' },
                { label: 'Assembler/Fabricator', value: 'AQJ' },
                { label: 'Inspector/Quality Control', value: 'AQK' },
                { label: 'Supervisor/Manager', value: 'AQP' },
                { label: 'Other - Production/Manufacturing', value: 'AQZ' }
              ]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [
                { label: 'Custodian/Janitor', value: 'AR1' },
                { label: 'Landscape/Grounds Maintenance', value: 'AR7' },
                { label: 'Laborer/Helper', value: 'ARA' },
                { label: 'Repair/Mechanic', value: 'ARB' },
                { label: 'Other - Repair/Maintenance/Grounds', value: 'ARZ' }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                { label: 'Athlete', value: 'AS3' },
                { label: 'Admin/Activity Assistant', value: 'ASJ' },
                { label: 'Fitness Trainer/Instructor', value: 'ASK' },
                { label: 'Supervisor/Manager', value: 'ASL' },
                { label: 'Other - Sports/Recreation', value: 'ASZ' }
              ]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Driver (All Vehicles)', value: 'ATQ' },
                { label: 'Packer/Mover', value: 'ATR' },
                { label: 'Supervisor/Manager', value: 'ATS' },
                { label: 'Other - Travel/Transportation/Storage', value: 'ATZ' }
              ]
            },
            {
              industry: 'Other',
              occupations: []
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ]
        },
        PA: {
          recentCoverageLimits: [
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          occupation: [
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: '10' }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: '20' }]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: '30' }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: '40' }]
            },
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                { label: 'Farm/Ranch Owner', value: 'AA4' },
                { label: 'Laborer/Worker', value: 'AA8' },
                { label: 'Supervisor/Manager', value: 'AAE' },
                { label: 'Inspector/Quality Control', value: 'AAG' },
                { label: 'Other - Agriculture/Forestry/Fishing', value: 'AAZ' }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                { label: 'Editor', value: 'ABA' },
                { label: 'Artist/Writer', value: 'ABL' },
                { label: 'Composer/Director/Producer', value: 'ABM' },
                { label: 'Designer/Photographer', value: 'ABN' },
                { label: 'Performer', value: 'ABP' },
                { label: 'Supervisor/Manager', value: 'ABQ' },
                { label: 'Other - Art/Design/Media', value: 'ABZ' }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                { label: 'Analyst', value: 'AC2' },
                { label: 'Appraiser - Real Estate', value: 'AC3' },
                { label: 'Broker', value: 'AC5' },
                { label: 'CSR/Teller', value: 'ACB' },
                { label: 'Manager - Property', value: 'ACL' },
                { label: 'Accountant', value: 'ACS' },
                { label: 'Billing & Collections', value: 'ACT' },
                { label: 'Finance/Insurance/Real Estate Rep', value: 'ACU' },
                { label: 'Financial Advisor/Investor', value: 'ACV' },
                { label: 'Loan Officer', value: 'ACW' },
                { label: 'Sales/Purchasing Rep', value: 'ACX' },
                { label: 'Supervisor/Manager', value: 'ACY' },
                { label: 'Other - Banking/Finance/Real Estate', value: 'ACZ' }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                { label: 'Account Executive', value: 'AD0' },
                { label: 'Administrative Assistant', value: 'AD1' },
                { label: 'Cashier/Checker', value: 'AD3' },
                { label: 'Consultant', value: 'AD5' },
                { label: 'Customer Service Representative', value: 'AD6' },
                { label: 'Accountant/Auditor', value: 'ADR' },
                { label: 'Agent/Broker', value: 'ADS' },
                { label: 'Bookkeeper', value: 'ADV' },
                { label: 'General Office Support', value: 'ADW' },
                { label: 'Human Resource (HR) Specialist', value: 'ADX' },
                { label: 'Manager - Executive/GM', value: 'BD0' },
                { label: 'Manager - Other than Executive/GM', value: 'BD1' },
                { label: 'Manufacturing Rep', value: 'BD2' },
                { label: 'Marketing Specialist', value: 'BD3' },
                { label: 'Receptionist', value: 'BD7' },
                { label: 'Retail Salesperson', value: 'BD8' },
                { label: 'Supervisor/Manager (Retail)', value: 'BDA' },
                { label: 'Supervisor/Manager (Non - Retail)', value: 'BDB' },
                { label: 'Vehicle/Equip Rental Rep', value: 'BDC' },
                { label: 'Other - Sales', value: 'BDZ' },
                { label: 'Other - Business/Office', value: 'CDZ' }
              ]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [
                { label: 'Electrician/Linesman', value: 'AEA' },
                { label: 'Machine/Equipment Operator', value: 'AEC' },
                { label: 'HVAC Technician', value: 'AEG' },
                { label: 'Laborer/Worker', value: 'AEJ' },
                { label: 'Plumber', value: 'AEQ' },
                { label: 'Field Service Technician', value: 'AEU' },
                { label: 'Foreman/Manager/Inspector', value: 'AEV' },
                { label: 'Maintenance/Custodian', value: 'AEW' },
                { label: 'Other - Construction/Energy/Mining', value: 'AEZ' }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                { label: 'Counselor', value: 'AF4' },
                { label: 'Librarian/Curator', value: 'AF7' },
                { label: 'Principal/Administrator', value: 'AF8' },
                { label: 'Teacher, K - 12', value: 'AFD' },
                { label: 'Assistant/Aide', value: 'AFG' },
                { label: 'College Professor', value: 'AFH' },
                { label: 'Teacher - Vocational', value: 'AFK' },
                { label: 'Other - Education/Library', value: 'AFZ' }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer', value: 'AG8' },
                { label: 'Technician', value: 'AGK' },
                { label: 'Analyst - Engineering', value: 'AGL' },
                { label: 'Architect/Drafter', value: 'AGN' },
                { label: 'Scientist/Researcher', value: 'AGP' },
                { label: 'Supervisor/Manager', value: 'AGQ' },
                { label: 'Other - Engineer/Architect', value: 'AGZ' }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [
                { label: 'Bartender', value: 'AH1' },
                { label: 'Caterer', value: 'AH4' },
                { label: 'Chef', value: 'AH5' },
                { label: 'Cook/Worker - Fast Food', value: 'AH9' },
                { label: 'Desk Clerk', value: 'AHB' },
                { label: 'Dishwasher', value: 'AHC' },
                { label: 'Housekeeper/Maid', value: 'AHF' },
                { label: 'Food Preparer/Server', value: 'AHL' },
                { label: 'Manager', value: 'AHM' },
                { label: 'Other - Hotel Services', value: 'AHR' },
                { label: 'Other - Food Services', value: 'AHS' }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                { label: 'Military - Enlisted (E1 - E4)', value: 'AJ9' },
                { label: 'Military - Warrant Officer (W1 - W5)', value: 'AJF' },
                { label: 'US Postal Worker', value: 'AJM' },
                { label: 'Auditor/Analyst/Examiner', value: 'AJN' },
                { label: 'Military - Officer (E5 - O10)', value: 'AJP' },
                { label: 'Supervisor/Manager', value: 'AJQ' },
                { label: 'Other - Government/Military', value: 'AJZ' }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                { label: 'Analyst', value: 'AK1' },
                { label: 'Director/Administrator', value: 'AK3' },
                { label: 'Computer Programmer', value: 'AKA' },
                { label: 'Supervisor/Manager', value: 'AKH' },
                { label: 'Technician', value: 'AKJ' },
                { label: 'Other - Information Technology', value: 'AKZ' }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                { label: 'Agent/Broker', value: 'AL4' },
                { label: 'Claims Adjuster/Investigator', value: 'AL7' },
                { label: 'Supervisor/Manager', value: 'ALJ' },
                { label: 'Underwriter/Actuary', value: 'ALK' },
                { label: 'Other - Insurance', value: 'ALZ' }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Correctional or Probation Officer', value: 'AM4' },
                { label: 'Legal Support Staff', value: 'AMG' },
                { label: 'Security Guard', value: 'AMN' },
                { label: 'Lawyer/Judge/Mediator', value: 'AMQ' },
                { label: 'Fire Fighter', value: 'AMR' },
                { label: 'Police/Highway Patrol/Sheriff', value: 'AMS' },
                { label: 'Detective/Investigator', value: 'AMT' },
                { label: 'Supervisor/Manager', value: 'AMU' },
                { label: 'Other - Legal/Law Enf/Security', value: 'AMZ' }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Chiropractor', value: 'AN2' },
                { label: 'Clergy', value: 'AN3' },
                { label: 'Licensed Nurse (LPN)', value: 'ANE' },
                { label: 'Registered Nurse (RN)', value: 'ANF' },
                { label: 'Paramedic/E.M.T.', value: 'ANK' },
                { label: 'Pharmacist', value: 'ANL' },
                { label: 'Social Worker', value: 'ANQ' },
                { label: 'Veterinarian', value: 'ANU' },
                { label: 'Aide/Attendant', value: 'ANV' },
                { label: 'Assistant/Technician/Therapist', value: 'ANW' },
                { label: 'Dentist/Orthodontist', value: 'ANY' },
                { label: 'Doctor/Surgeon', value: 'BN0' },
                { label: 'Psychologist/Therapist', value: 'BN1' },
                {
                  label: 'Other - Medical/Social Services/Religion',
                  value: 'BNZ'
                }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [
                { label: 'Child or Elderly Care Provider', value: 'AP0' },
                { label: 'Dry Cleaning/Laundry Servicer', value: 'AP1' },
                { label: 'Hair/Personal Care Provider', value: 'AP2' },
                { label: 'Cleaner/Housekeeper', value: 'AP3' },
                { label: 'Pet Service Provider', value: 'AP7' },
                { label: 'Other - Personal Care/Service', value: 'APZ' }
              ]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [
                { label: 'Machine Operator', value: 'AQ7' },
                { label: 'Assembler/Fabricator', value: 'AQJ' },
                { label: 'Inspector/Quality Control', value: 'AQK' },
                { label: 'Supervisor/Manager', value: 'AQP' },
                { label: 'Other - Production/Manufacturing', value: 'AQZ' }
              ]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [
                { label: 'Custodian/Janitor', value: 'AR1' },
                { label: 'Landscape/Grounds Maintenance', value: 'AR7' },
                { label: 'Laborer/Helper', value: 'ARA' },
                { label: 'Repair/Mechanic', value: 'ARB' },
                { label: 'Other - Repair/Maintenance/Grounds', value: 'ARZ' }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                { label: 'Athlete', value: 'AS3' },
                { label: 'Admin/Activity Assistant', value: 'ASJ' },
                { label: 'Fitness Trainer/Instructor', value: 'ASK' },
                { label: 'Supervisor/Manager', value: 'ASL' },
                { label: 'Other - Sports/Recreation', value: 'ASZ' }
              ]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Driver (All Vehicles)', value: 'ATQ' },
                { label: 'Packer/Mover', value: 'ATR' },
                { label: 'Supervisor/Manager', value: 'ATS' },
                { label: 'Other - Travel/Transportation/Storage', value: 'ATZ' }
              ]
            },
            {
              industry: 'Other',
              occupations: []
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ]
        },
        TN: {
          recentCoverageLimits: [
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          occupation: [
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: '10' }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: '20' }]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: '30' }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: '40' }]
            },
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                { label: 'Farm/Ranch Owner', value: 'AA4' },
                { label: 'Laborer/Worker', value: 'AA8' },
                { label: 'Supervisor/Manager', value: 'AAE' },
                { label: 'Inspector/Quality Control', value: 'AAG' },
                { label: 'Other - Agriculture/Forestry/Fishing', value: 'AAZ' }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                { label: 'Editor', value: 'ABA' },
                { label: 'Artist/Writer', value: 'ABL' },
                { label: 'Composer/Director/Producer', value: 'ABM' },
                { label: 'Designer/Photographer', value: 'ABN' },
                { label: 'Performer', value: 'ABP' },
                { label: 'Supervisor/Manager', value: 'ABQ' },
                { label: 'Other - Art/Design/Media', value: 'ABZ' }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                { label: 'Analyst', value: 'AC2' },
                { label: 'Appraiser - Real Estate', value: 'AC3' },
                { label: 'Broker', value: 'AC5' },
                { label: 'CSR/Teller', value: 'ACB' },
                { label: 'Manager - Property', value: 'ACL' },
                { label: 'Accountant', value: 'ACS' },
                { label: 'Billing & Collections', value: 'ACT' },
                { label: 'Finance/Insurance/Real Estate Rep', value: 'ACU' },
                { label: 'Financial Advisor/Investor', value: 'ACV' },
                { label: 'Loan Officer', value: 'ACW' },
                { label: 'Sales/Purchasing Rep', value: 'ACX' },
                { label: 'Supervisor/Manager', value: 'ACY' },
                { label: 'Other - Banking/Finance/Real Estate', value: 'ACZ' }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                { label: 'Account Executive', value: 'AD0' },
                { label: 'Administrative Assistant', value: 'AD1' },
                { label: 'Cashier/Checker', value: 'AD3' },
                { label: 'Consultant', value: 'AD5' },
                { label: 'Customer Service Representative', value: 'AD6' },
                { label: 'Accountant/Auditor', value: 'ADR' },
                { label: 'Agent/Broker', value: 'ADS' },
                { label: 'Bookkeeper', value: 'ADV' },
                { label: 'General Office Support', value: 'ADW' },
                { label: 'Human Resource (HR) Specialist', value: 'ADX' },
                { label: 'Manager - Executive/GM', value: 'BD0' },
                { label: 'Manager - Other than Executive/GM', value: 'BD1' },
                { label: 'Manufacturing Rep', value: 'BD2' },
                { label: 'Marketing Specialist', value: 'BD3' },
                { label: 'Receptionist', value: 'BD7' },
                { label: 'Retail Salesperson', value: 'BD8' },
                { label: 'Supervisor/Manager (Retail)', value: 'BDA' },
                { label: 'Supervisor/Manager (Non - Retail)', value: 'BDB' },
                { label: 'Vehicle/Equip Rental Rep', value: 'BDC' },
                { label: 'Other - Sales', value: 'BDZ' },
                { label: 'Other - Business/Office', value: 'CDZ' }
              ]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [
                { label: 'Electrician/Linesman', value: 'AEA' },
                { label: 'Machine/Equipment Operator', value: 'AEC' },
                { label: 'HVAC Technician', value: 'AEG' },
                { label: 'Laborer/Worker', value: 'AEJ' },
                { label: 'Plumber', value: 'AEQ' },
                { label: 'Field Service Technician', value: 'AEU' },
                { label: 'Foreman/Manager/Inspector', value: 'AEV' },
                { label: 'Maintenance/Custodian', value: 'AEW' },
                { label: 'Other - Construction/Energy/Mining', value: 'AEZ' }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                { label: 'Counselor', value: 'AF4' },
                { label: 'Librarian/Curator', value: 'AF7' },
                { label: 'Principal/Administrator', value: 'AF8' },
                { label: 'Teacher, K - 12', value: 'AFD' },
                { label: 'Assistant/Aide', value: 'AFG' },
                { label: 'College Professor', value: 'AFH' },
                { label: 'Teacher - Vocational', value: 'AFK' },
                { label: 'Other - Education/Library', value: 'AFZ' }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer', value: 'AG8' },
                { label: 'Technician', value: 'AGK' },
                { label: 'Analyst - Engineering', value: 'AGL' },
                { label: 'Architect/Drafter', value: 'AGN' },
                { label: 'Scientist/Researcher', value: 'AGP' },
                { label: 'Supervisor/Manager', value: 'AGQ' },
                { label: 'Other - Engineer/Architect', value: 'AGZ' }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [
                { label: 'Bartender', value: 'AH1' },
                { label: 'Caterer', value: 'AH4' },
                { label: 'Chef', value: 'AH5' },
                { label: 'Cook/Worker - Fast Food', value: 'AH9' },
                { label: 'Desk Clerk', value: 'AHB' },
                { label: 'Dishwasher', value: 'AHC' },
                { label: 'Housekeeper/Maid', value: 'AHF' },
                { label: 'Food Preparer/Server', value: 'AHL' },
                { label: 'Manager', value: 'AHM' },
                { label: 'Other - Hotel Services', value: 'AHR' },
                { label: 'Other - Food Services', value: 'AHS' }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                { label: 'Military - Enlisted (E1 - E4)', value: 'AJ9' },
                { label: 'Military - Warrant Officer (W1 - W5)', value: 'AJF' },
                { label: 'US Postal Worker', value: 'AJM' },
                { label: 'Auditor/Analyst/Examiner', value: 'AJN' },
                { label: 'Military - Officer (E5 - O10)', value: 'AJP' },
                { label: 'Supervisor/Manager', value: 'AJQ' },
                { label: 'Other - Government/Military', value: 'AJZ' }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                { label: 'Analyst', value: 'AK1' },
                { label: 'Director/Administrator', value: 'AK3' },
                { label: 'Computer Programmer', value: 'AKA' },
                { label: 'Supervisor/Manager', value: 'AKH' },
                { label: 'Technician', value: 'AKJ' },
                { label: 'Other - Information Technology', value: 'AKZ' }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                { label: 'Agent/Broker', value: 'AL4' },
                { label: 'Claims Adjuster/Investigator', value: 'AL7' },
                { label: 'Supervisor/Manager', value: 'ALJ' },
                { label: 'Underwriter/Actuary', value: 'ALK' },
                { label: 'Other - Insurance', value: 'ALZ' }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Correctional or Probation Officer', value: 'AM4' },
                { label: 'Legal Support Staff', value: 'AMG' },
                { label: 'Security Guard', value: 'AMN' },
                { label: 'Lawyer/Judge/Mediator', value: 'AMQ' },
                { label: 'Fire Fighter', value: 'AMR' },
                { label: 'Police/Highway Patrol/Sheriff', value: 'AMS' },
                { label: 'Detective/Investigator', value: 'AMT' },
                { label: 'Supervisor/Manager', value: 'AMU' },
                { label: 'Other - Legal/Law Enf/Security', value: 'AMZ' }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Chiropractor', value: 'AN2' },
                { label: 'Clergy', value: 'AN3' },
                { label: 'Licensed Nurse (LPN)', value: 'ANE' },
                { label: 'Registered Nurse (RN)', value: 'ANF' },
                { label: 'Paramedic/E.M.T.', value: 'ANK' },
                { label: 'Pharmacist', value: 'ANL' },
                { label: 'Social Worker', value: 'ANQ' },
                { label: 'Veterinarian', value: 'ANU' },
                { label: 'Aide/Attendant', value: 'ANV' },
                { label: 'Assistant/Technician/Therapist', value: 'ANW' },
                { label: 'Dentist/Orthodontist', value: 'ANY' },
                { label: 'Doctor/Surgeon', value: 'BN0' },
                { label: 'Psychologist/Therapist', value: 'BN1' },
                {
                  label: 'Other - Medical/Social Services/Religion',
                  value: 'BNZ'
                }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [
                { label: 'Child or Elderly Care Provider', value: 'AP0' },
                { label: 'Dry Cleaning/Laundry Servicer', value: 'AP1' },
                { label: 'Hair/Personal Care Provider', value: 'AP2' },
                { label: 'Cleaner/Housekeeper', value: 'AP3' },
                { label: 'Pet Service Provider', value: 'AP7' },
                { label: 'Other - Personal Care/Service', value: 'APZ' }
              ]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [
                { label: 'Machine Operator', value: 'AQ7' },
                { label: 'Assembler/Fabricator', value: 'AQJ' },
                { label: 'Inspector/Quality Control', value: 'AQK' },
                { label: 'Supervisor/Manager', value: 'AQP' },
                { label: 'Other - Production/Manufacturing', value: 'AQZ' }
              ]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [
                { label: 'Custodian/Janitor', value: 'AR1' },
                { label: 'Landscape/Grounds Maintenance', value: 'AR7' },
                { label: 'Laborer/Helper', value: 'ARA' },
                { label: 'Repair/Mechanic', value: 'ARB' },
                { label: 'Other - Repair/Maintenance/Grounds', value: 'ARZ' }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                { label: 'Athlete', value: 'AS3' },
                { label: 'Admin/Activity Assistant', value: 'ASJ' },
                { label: 'Fitness Trainer/Instructor', value: 'ASK' },
                { label: 'Supervisor/Manager', value: 'ASL' },
                { label: 'Other - Sports/Recreation', value: 'ASZ' }
              ]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Driver (All Vehicles)', value: 'ATQ' },
                { label: 'Packer/Mover', value: 'ATR' },
                { label: 'Supervisor/Manager', value: 'ATS' },
                { label: 'Other - Travel/Transportation/Storage', value: 'ATZ' }
              ]
            },
            {
              industry: 'Other',
              occupations: []
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ]
        },
        TX: {
          recentCoverageLimits: [
            {
              label: '$30,000/$60,000',
              value: '30/60'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          occupation: [
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: '10' }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: '20' }]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: '30' }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: '40' }]
            },
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                { label: 'Farm/Ranch Owner', value: 'AA4' },
                { label: 'Laborer/Worker', value: 'AA8' },
                { label: 'Supervisor/Manager', value: 'AAE' },
                { label: 'Inspector/Quality Control', value: 'AAG' },
                { label: 'Other - Agriculture/Forestry/Fishing', value: 'AAZ' }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                { label: 'Editor', value: 'ABA' },
                { label: 'Artist/Writer', value: 'ABL' },
                { label: 'Composer/Director/Producer', value: 'ABM' },
                { label: 'Designer/Photographer', value: 'ABN' },
                { label: 'Performer', value: 'ABP' },
                { label: 'Supervisor/Manager', value: 'ABQ' },
                { label: 'Other - Art/Design/Media', value: 'ABZ' }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                { label: 'Analyst', value: 'AC2' },
                { label: 'Appraiser - Real Estate', value: 'AC3' },
                { label: 'Broker', value: 'AC5' },
                { label: 'CSR/Teller', value: 'ACB' },
                { label: 'Manager - Property', value: 'ACL' },
                { label: 'Accountant', value: 'ACS' },
                { label: 'Billing & Collections', value: 'ACT' },
                { label: 'Finance/Insurance/Real Estate Rep', value: 'ACU' },
                { label: 'Financial Advisor/Investor', value: 'ACV' },
                { label: 'Loan Officer', value: 'ACW' },
                { label: 'Sales/Purchasing Rep', value: 'ACX' },
                { label: 'Supervisor/Manager', value: 'ACY' },
                { label: 'Other - Banking/Finance/Real Estate', value: 'ACZ' }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                { label: 'Account Executive', value: 'AD0' },
                { label: 'Administrative Assistant', value: 'AD1' },
                { label: 'Cashier/Checker', value: 'AD3' },
                { label: 'Consultant', value: 'AD5' },
                { label: 'Customer Service Representative', value: 'AD6' },
                { label: 'Accountant/Auditor', value: 'ADR' },
                { label: 'Agent/Broker', value: 'ADS' },
                { label: 'Bookkeeper', value: 'ADV' },
                { label: 'General Office Support', value: 'ADW' },
                { label: 'Human Resource (HR) Specialist', value: 'ADX' },
                { label: 'Manager - Executive/GM', value: 'BD0' },
                { label: 'Manager - Other than Executive/GM', value: 'BD1' },
                { label: 'Manufacturing Rep', value: 'BD2' },
                { label: 'Marketing Specialist', value: 'BD3' },
                { label: 'Receptionist', value: 'BD7' },
                { label: 'Retail Salesperson', value: 'BD8' },
                { label: 'Supervisor/Manager (Retail)', value: 'BDA' },
                { label: 'Supervisor/Manager (Non - Retail)', value: 'BDB' },
                { label: 'Vehicle/Equip Rental Rep', value: 'BDC' },
                { label: 'Other - Sales', value: 'BDZ' },
                { label: 'Other - Business/Office', value: 'CDZ' }
              ]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [
                { label: 'Electrician/Linesman', value: 'AEA' },
                { label: 'Machine/Equipment Operator', value: 'AEC' },
                { label: 'HVAC Technician', value: 'AEG' },
                { label: 'Laborer/Worker', value: 'AEJ' },
                { label: 'Plumber', value: 'AEQ' },
                { label: 'Field Service Technician', value: 'AEU' },
                { label: 'Foreman/Manager/Inspector', value: 'AEV' },
                { label: 'Maintenance/Custodian', value: 'AEW' },
                { label: 'Other - Construction/Energy/Mining', value: 'AEZ' }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                { label: 'Counselor', value: 'AF4' },
                { label: 'Librarian/Curator', value: 'AF7' },
                { label: 'Principal/Administrator', value: 'AF8' },
                { label: 'Teacher, K - 12', value: 'AFD' },
                { label: 'Assistant/Aide', value: 'AFG' },
                { label: 'College Professor', value: 'AFH' },
                { label: 'Teacher - Vocational', value: 'AFK' },
                { label: 'Other - Education/Library', value: 'AFZ' }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer', value: 'AG8' },
                { label: 'Technician', value: 'AGK' },
                { label: 'Analyst - Engineering', value: 'AGL' },
                { label: 'Architect/Drafter', value: 'AGN' },
                { label: 'Scientist/Researcher', value: 'AGP' },
                { label: 'Supervisor/Manager', value: 'AGQ' },
                { label: 'Other - Engineer/Architect', value: 'AGZ' }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [
                { label: 'Bartender', value: 'AH1' },
                { label: 'Caterer', value: 'AH4' },
                { label: 'Chef', value: 'AH5' },
                { label: 'Cook/Worker - Fast Food', value: 'AH9' },
                { label: 'Desk Clerk', value: 'AHB' },
                { label: 'Dishwasher', value: 'AHC' },
                { label: 'Housekeeper/Maid', value: 'AHF' },
                { label: 'Food Preparer/Server', value: 'AHL' },
                { label: 'Manager', value: 'AHM' },
                { label: 'Other - Hotel Services', value: 'AHR' },
                { label: 'Other - Food Services', value: 'AHS' }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                { label: 'Military - Enlisted (E1 - E4)', value: 'AJ9' },
                { label: 'Military - Warrant Officer (W1 - W5)', value: 'AJF' },
                { label: 'US Postal Worker', value: 'AJM' },
                { label: 'Auditor/Analyst/Examiner', value: 'AJN' },
                { label: 'Military - Officer (E5 - O10)', value: 'AJP' },
                { label: 'Supervisor/Manager', value: 'AJQ' },
                { label: 'Other - Government/Military', value: 'AJZ' }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                { label: 'Analyst', value: 'AK1' },
                { label: 'Director/Administrator', value: 'AK3' },
                { label: 'Computer Programmer', value: 'AKA' },
                { label: 'Supervisor/Manager', value: 'AKH' },
                { label: 'Technician', value: 'AKJ' },
                { label: 'Other - Information Technology', value: 'AKZ' }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                { label: 'Agent/Broker', value: 'AL4' },
                { label: 'Claims Adjuster/Investigator', value: 'AL7' },
                { label: 'Supervisor/Manager', value: 'ALJ' },
                { label: 'Underwriter/Actuary', value: 'ALK' },
                { label: 'Other - Insurance', value: 'ALZ' }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Correctional or Probation Officer', value: 'AM4' },
                { label: 'Legal Support Staff', value: 'AMG' },
                { label: 'Security Guard', value: 'AMN' },
                { label: 'Lawyer/Judge/Mediator', value: 'AMQ' },
                { label: 'Fire Fighter', value: 'AMR' },
                { label: 'Police/Highway Patrol/Sheriff', value: 'AMS' },
                { label: 'Detective/Investigator', value: 'AMT' },
                { label: 'Supervisor/Manager', value: 'AMU' },
                { label: 'Other - Legal/Law Enf/Security', value: 'AMZ' }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Chiropractor', value: 'AN2' },
                { label: 'Clergy', value: 'AN3' },
                { label: 'Licensed Nurse (LPN)', value: 'ANE' },
                { label: 'Registered Nurse (RN)', value: 'ANF' },
                { label: 'Paramedic/E.M.T.', value: 'ANK' },
                { label: 'Pharmacist', value: 'ANL' },
                { label: 'Social Worker', value: 'ANQ' },
                { label: 'Veterinarian', value: 'ANU' },
                { label: 'Aide/Attendant', value: 'ANV' },
                { label: 'Assistant/Technician/Therapist', value: 'ANW' },
                { label: 'Dentist/Orthodontist', value: 'ANY' },
                { label: 'Doctor/Surgeon', value: 'BN0' },
                { label: 'Psychologist/Therapist', value: 'BN1' },
                {
                  label: 'Other - Medical/Social Services/Religion',
                  value: 'BNZ'
                }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [
                { label: 'Child or Elderly Care Provider', value: 'AP0' },
                { label: 'Dry Cleaning/Laundry Servicer', value: 'AP1' },
                { label: 'Hair/Personal Care Provider', value: 'AP2' },
                { label: 'Cleaner/Housekeeper', value: 'AP3' },
                { label: 'Pet Service Provider', value: 'AP7' },
                { label: 'Other - Personal Care/Service', value: 'APZ' }
              ]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [
                { label: 'Machine Operator', value: 'AQ7' },
                { label: 'Assembler/Fabricator', value: 'AQJ' },
                { label: 'Inspector/Quality Control', value: 'AQK' },
                { label: 'Supervisor/Manager', value: 'AQP' },
                { label: 'Other - Production/Manufacturing', value: 'AQZ' }
              ]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [
                { label: 'Custodian/Janitor', value: 'AR1' },
                { label: 'Landscape/Grounds Maintenance', value: 'AR7' },
                { label: 'Laborer/Helper', value: 'ARA' },
                { label: 'Repair/Mechanic', value: 'ARB' },
                { label: 'Other - Repair/Maintenance/Grounds', value: 'ARZ' }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                { label: 'Athlete', value: 'AS3' },
                { label: 'Admin/Activity Assistant', value: 'ASJ' },
                { label: 'Fitness Trainer/Instructor', value: 'ASK' },
                { label: 'Supervisor/Manager', value: 'ASL' },
                { label: 'Other - Sports/Recreation', value: 'ASZ' }
              ]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Driver (All Vehicles)', value: 'ATQ' },
                { label: 'Packer/Mover', value: 'ATR' },
                { label: 'Supervisor/Manager', value: 'ATS' },
                { label: 'Other - Travel/Transportation/Storage', value: 'ATZ' }
              ]
            },
            {
              industry: 'Other',
              occupations: []
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ]
        },
        UT: {
          recentCoverageLimits: [
            {
              label: '$30,000/$65,000',
              value: '30/65'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' }
          ],
          occupation: [
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: '10' }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: '20' }]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: '30' }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: '40' }]
            },
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                { label: 'Farm/Ranch Owner', value: 'AA4' },
                { label: 'Laborer/Worker', value: 'AA8' },
                { label: 'Supervisor/Manager', value: 'AAE' },
                { label: 'Inspector/Quality Control', value: 'AAG' },
                { label: 'Other - Agriculture/Forestry/Fishing', value: 'AAZ' }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                { label: 'Editor', value: 'ABA' },
                { label: 'Artist/Writer', value: 'ABL' },
                { label: 'Composer/Director/Producer', value: 'ABM' },
                { label: 'Designer/Photographer', value: 'ABN' },
                { label: 'Performer', value: 'ABP' },
                { label: 'Supervisor/Manager', value: 'ABQ' },
                { label: 'Other - Art/Design/Media', value: 'ABZ' }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                { label: 'Analyst', value: 'AC2' },
                { label: 'Appraiser - Real Estate', value: 'AC3' },
                { label: 'Broker', value: 'AC5' },
                { label: 'CSR/Teller', value: 'ACB' },
                { label: 'Manager - Property', value: 'ACL' },
                { label: 'Accountant', value: 'ACS' },
                { label: 'Billing & Collections', value: 'ACT' },
                { label: 'Finance/Insurance/Real Estate Rep', value: 'ACU' },
                { label: 'Financial Advisor/Investor', value: 'ACV' },
                { label: 'Loan Officer', value: 'ACW' },
                { label: 'Sales/Purchasing Rep', value: 'ACX' },
                { label: 'Supervisor/Manager', value: 'ACY' },
                { label: 'Other - Banking/Finance/Real Estate', value: 'ACZ' }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                { label: 'Account Executive', value: 'AD0' },
                { label: 'Administrative Assistant', value: 'AD1' },
                { label: 'Cashier/Checker', value: 'AD3' },
                { label: 'Consultant', value: 'AD5' },
                { label: 'Customer Service Representative', value: 'AD6' },
                { label: 'Accountant/Auditor', value: 'ADR' },
                { label: 'Agent/Broker', value: 'ADS' },
                { label: 'Bookkeeper', value: 'ADV' },
                { label: 'General Office Support', value: 'ADW' },
                { label: 'Human Resource (HR) Specialist', value: 'ADX' },
                { label: 'Manager - Executive/GM', value: 'BD0' },
                { label: 'Manager - Other than Executive/GM', value: 'BD1' },
                { label: 'Manufacturing Rep', value: 'BD2' },
                { label: 'Marketing Specialist', value: 'BD3' },
                { label: 'Receptionist', value: 'BD7' },
                { label: 'Retail Salesperson', value: 'BD8' },
                { label: 'Supervisor/Manager (Retail)', value: 'BDA' },
                { label: 'Supervisor/Manager (Non - Retail)', value: 'BDB' },
                { label: 'Vehicle/Equip Rental Rep', value: 'BDC' },
                { label: 'Other - Sales', value: 'BDZ' },
                { label: 'Other - Business/Office', value: 'CDZ' }
              ]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [
                { label: 'Electrician/Linesman', value: 'AEA' },
                { label: 'Machine/Equipment Operator', value: 'AEC' },
                { label: 'HVAC Technician', value: 'AEG' },
                { label: 'Laborer/Worker', value: 'AEJ' },
                { label: 'Plumber', value: 'AEQ' },
                { label: 'Field Service Technician', value: 'AEU' },
                { label: 'Foreman/Manager/Inspector', value: 'AEV' },
                { label: 'Maintenance/Custodian', value: 'AEW' },
                { label: 'Other - Construction/Energy/Mining', value: 'AEZ' }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                { label: 'Counselor', value: 'AF4' },
                { label: 'Librarian/Curator', value: 'AF7' },
                { label: 'Principal/Administrator', value: 'AF8' },
                { label: 'Teacher, K - 12', value: 'AFD' },
                { label: 'Assistant/Aide', value: 'AFG' },
                { label: 'College Professor', value: 'AFH' },
                { label: 'Teacher - Vocational', value: 'AFK' },
                { label: 'Other - Education/Library', value: 'AFZ' }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer', value: 'AG8' },
                { label: 'Technician', value: 'AGK' },
                { label: 'Analyst - Engineering', value: 'AGL' },
                { label: 'Architect/Drafter', value: 'AGN' },
                { label: 'Scientist/Researcher', value: 'AGP' },
                { label: 'Supervisor/Manager', value: 'AGQ' },
                { label: 'Other - Engineer/Architect', value: 'AGZ' }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [
                { label: 'Bartender', value: 'AH1' },
                { label: 'Caterer', value: 'AH4' },
                { label: 'Chef', value: 'AH5' },
                { label: 'Cook/Worker - Fast Food', value: 'AH9' },
                { label: 'Desk Clerk', value: 'AHB' },
                { label: 'Dishwasher', value: 'AHC' },
                { label: 'Housekeeper/Maid', value: 'AHF' },
                { label: 'Food Preparer/Server', value: 'AHL' },
                { label: 'Manager', value: 'AHM' },
                { label: 'Other - Hotel Services', value: 'AHR' },
                { label: 'Other - Food Services', value: 'AHS' }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                { label: 'Military - Enlisted (E1 - E4)', value: 'AJ9' },
                { label: 'Military - Warrant Officer (W1 - W5)', value: 'AJF' },
                { label: 'US Postal Worker', value: 'AJM' },
                { label: 'Auditor/Analyst/Examiner', value: 'AJN' },
                { label: 'Military - Officer (E5 - O10)', value: 'AJP' },
                { label: 'Supervisor/Manager', value: 'AJQ' },
                { label: 'Other - Government/Military', value: 'AJZ' }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                { label: 'Analyst', value: 'AK1' },
                { label: 'Director/Administrator', value: 'AK3' },
                { label: 'Computer Programmer', value: 'AKA' },
                { label: 'Supervisor/Manager', value: 'AKH' },
                { label: 'Technician', value: 'AKJ' },
                { label: 'Other - Information Technology', value: 'AKZ' }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                { label: 'Agent/Broker', value: 'AL4' },
                { label: 'Claims Adjuster/Investigator', value: 'AL7' },
                { label: 'Supervisor/Manager', value: 'ALJ' },
                { label: 'Underwriter/Actuary', value: 'ALK' },
                { label: 'Other - Insurance', value: 'ALZ' }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Correctional or Probation Officer', value: 'AM4' },
                { label: 'Legal Support Staff', value: 'AMG' },
                { label: 'Security Guard', value: 'AMN' },
                { label: 'Lawyer/Judge/Mediator', value: 'AMQ' },
                { label: 'Fire Fighter', value: 'AMR' },
                { label: 'Police/Highway Patrol/Sheriff', value: 'AMS' },
                { label: 'Detective/Investigator', value: 'AMT' },
                { label: 'Supervisor/Manager', value: 'AMU' },
                { label: 'Other - Legal/Law Enf/Security', value: 'AMZ' }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Chiropractor', value: 'AN2' },
                { label: 'Clergy', value: 'AN3' },
                { label: 'Licensed Nurse (LPN)', value: 'ANE' },
                { label: 'Registered Nurse (RN)', value: 'ANF' },
                { label: 'Paramedic/E.M.T.', value: 'ANK' },
                { label: 'Pharmacist', value: 'ANL' },
                { label: 'Social Worker', value: 'ANQ' },
                { label: 'Veterinarian', value: 'ANU' },
                { label: 'Aide/Attendant', value: 'ANV' },
                { label: 'Assistant/Technician/Therapist', value: 'ANW' },
                { label: 'Dentist/Orthodontist', value: 'ANY' },
                { label: 'Doctor/Surgeon', value: 'BN0' },
                { label: 'Psychologist/Therapist', value: 'BN1' },
                {
                  label: 'Other - Medical/Social Services/Religion',
                  value: 'BNZ'
                }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [
                { label: 'Child or Elderly Care Provider', value: 'AP0' },
                { label: 'Dry Cleaning/Laundry Servicer', value: 'AP1' },
                { label: 'Hair/Personal Care Provider', value: 'AP2' },
                { label: 'Cleaner/Housekeeper', value: 'AP3' },
                { label: 'Pet Service Provider', value: 'AP7' },
                { label: 'Other - Personal Care/Service', value: 'APZ' }
              ]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [
                { label: 'Machine Operator', value: 'AQ7' },
                { label: 'Assembler/Fabricator', value: 'AQJ' },
                { label: 'Inspector/Quality Control', value: 'AQK' },
                { label: 'Supervisor/Manager', value: 'AQP' },
                { label: 'Other - Production/Manufacturing', value: 'AQZ' }
              ]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [
                { label: 'Custodian/Janitor', value: 'AR1' },
                { label: 'Landscape/Grounds Maintenance', value: 'AR7' },
                { label: 'Laborer/Helper', value: 'ARA' },
                { label: 'Repair/Mechanic', value: 'ARB' },
                { label: 'Other - Repair/Maintenance/Grounds', value: 'ARZ' }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                { label: 'Athlete', value: 'AS3' },
                { label: 'Admin/Activity Assistant', value: 'ASJ' },
                { label: 'Fitness Trainer/Instructor', value: 'ASK' },
                { label: 'Supervisor/Manager', value: 'ASL' },
                { label: 'Other - Sports/Recreation', value: 'ASZ' }
              ]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Driver (All Vehicles)', value: 'ATQ' },
                { label: 'Packer/Mover', value: 'ATR' },
                { label: 'Supervisor/Manager', value: 'ATS' },
                { label: 'Other - Travel/Transportation/Storage', value: 'ATZ' }
              ]
            },
            {
              industry: 'Other',
              occupations: []
            }
          ]
        },
        WI: {
          recentCoverageLimits: [
            {
              label: '$25,000/$50,000',
              value: '25/50'
            },
            {
              label: '$50,000/$100,000',
              value: '50/100'
            },
            {
              label: '100 CSL',
              value: '100 CSL'
            },
            {
              label: '$100,000/$300,000',
              value: '100/300'
            },
            {
              label: '300 CSL',
              value: '300 CSL'
            },
            {
              label: '$250,000/$500,000',
              value: '250/500'
            },
            {
              label: '500 CSL',
              value: '500 CSL'
            }
          ],
          occupation: [
            {
              industry: 'Homemaker (full - time)',
              occupations: [{ label: 'Homemaker (full - time)', value: '10' }]
            },
            {
              industry: 'Retired (full - time)',
              occupations: [{ label: 'Retired (full - time)', value: '20' }]
            },
            {
              industry: 'Unemployed',
              occupations: [{ label: 'Unemployed', value: '30' }]
            },
            {
              industry: 'Student (full - time)',
              occupations: [{ label: 'Student (full - time)', value: '40' }]
            },
            {
              industry: 'Agriculture/Forestry/Fishing',
              occupations: [
                { label: 'Farm/Ranch Owner', value: 'AA4' },
                { label: 'Laborer/Worker', value: 'AA8' },
                { label: 'Supervisor/Manager', value: 'AAE' },
                { label: 'Inspector/Quality Control', value: 'AAG' },
                { label: 'Other - Agriculture/Forestry/Fishing', value: 'AAZ' }
              ]
            },
            {
              industry: 'Art/Design/Media',
              occupations: [
                { label: 'Editor', value: 'ABA' },
                { label: 'Artist/Writer', value: 'ABL' },
                { label: 'Composer/Director/Producer', value: 'ABM' },
                { label: 'Designer/Photographer', value: 'ABN' },
                { label: 'Performer', value: 'ABP' },
                { label: 'Supervisor/Manager', value: 'ABQ' },
                { label: 'Other - Art/Design/Media', value: 'ABZ' }
              ]
            },
            {
              industry: 'Banking/Finance/Real Estate',
              occupations: [
                { label: 'Analyst', value: 'AC2' },
                { label: 'Appraiser - Real Estate', value: 'AC3' },
                { label: 'Broker', value: 'AC5' },
                { label: 'CSR/Teller', value: 'ACB' },
                { label: 'Manager - Property', value: 'ACL' },
                { label: 'Accountant', value: 'ACS' },
                { label: 'Billing & Collections', value: 'ACT' },
                { label: 'Finance/Insurance/Real Estate Rep', value: 'ACU' },
                { label: 'Financial Advisor/Investor', value: 'ACV' },
                { label: 'Loan Officer', value: 'ACW' },
                { label: 'Sales/Purchasing Rep', value: 'ACX' },
                { label: 'Supervisor/Manager', value: 'ACY' },
                { label: 'Other - Banking/Finance/Real Estate', value: 'ACZ' }
              ]
            },
            {
              industry: 'Business/Sales/Office',
              occupations: [
                { label: 'Account Executive', value: 'AD0' },
                { label: 'Administrative Assistant', value: 'AD1' },
                { label: 'Cashier/Checker', value: 'AD3' },
                { label: 'Consultant', value: 'AD5' },
                { label: 'Customer Service Representative', value: 'AD6' },
                { label: 'Accountant/Auditor', value: 'ADR' },
                { label: 'Agent/Broker', value: 'ADS' },
                { label: 'Bookkeeper', value: 'ADV' },
                { label: 'General Office Support', value: 'ADW' },
                { label: 'Human Resource (HR) Specialist', value: 'ADX' },
                { label: 'Manager - Executive/GM', value: 'BD0' },
                { label: 'Manager - Other than Executive/GM', value: 'BD1' },
                { label: 'Manufacturing Rep', value: 'BD2' },
                { label: 'Marketing Specialist', value: 'BD3' },
                { label: 'Receptionist', value: 'BD7' },
                { label: 'Retail Salesperson', value: 'BD8' },
                { label: 'Supervisor/Manager (Retail)', value: 'BDA' },
                { label: 'Supervisor/Manager (Non - Retail)', value: 'BDB' },
                { label: 'Vehicle/Equip Rental Rep', value: 'BDC' },
                { label: 'Other - Sales', value: 'BDZ' },
                { label: 'Other - Business/Office', value: 'CDZ' }
              ]
            },
            {
              industry: 'Construction/Energy/Mining',
              occupations: [
                { label: 'Electrician/Linesman', value: 'AEA' },
                { label: 'Machine/Equipment Operator', value: 'AEC' },
                { label: 'HVAC Technician', value: 'AEG' },
                { label: 'Laborer/Worker', value: 'AEJ' },
                { label: 'Plumber', value: 'AEQ' },
                { label: 'Field Service Technician', value: 'AEU' },
                { label: 'Foreman/Manager/Inspector', value: 'AEV' },
                { label: 'Maintenance/Custodian', value: 'AEW' },
                { label: 'Other - Construction/Energy/Mining', value: 'AEZ' }
              ]
            },
            {
              industry: 'Education/Library',
              occupations: [
                { label: 'Counselor', value: 'AF4' },
                { label: 'Librarian/Curator', value: 'AF7' },
                { label: 'Principal/Administrator', value: 'AF8' },
                { label: 'Teacher, K - 12', value: 'AFD' },
                { label: 'Assistant/Aide', value: 'AFG' },
                { label: 'College Professor', value: 'AFH' },
                { label: 'Teacher - Vocational', value: 'AFK' },
                { label: 'Other - Education/Library', value: 'AFZ' }
              ]
            },
            {
              industry: 'Engineer/Architect/Science/Math',
              occupations: [
                { label: 'Engineer', value: 'AG8' },
                { label: 'Technician', value: 'AGK' },
                { label: 'Analyst - Engineering', value: 'AGL' },
                { label: 'Architect/Drafter', value: 'AGN' },
                { label: 'Scientist/Researcher', value: 'AGP' },
                { label: 'Supervisor/Manager', value: 'AGQ' },
                { label: 'Other - Engineer/Architect', value: 'AGZ' }
              ]
            },
            {
              industry: 'Food Service/Hotel Services',
              occupations: [
                { label: 'Bartender', value: 'AH1' },
                { label: 'Caterer', value: 'AH4' },
                { label: 'Chef', value: 'AH5' },
                { label: 'Cook/Worker - Fast Food', value: 'AH9' },
                { label: 'Desk Clerk', value: 'AHB' },
                { label: 'Dishwasher', value: 'AHC' },
                { label: 'Housekeeper/Maid', value: 'AHF' },
                { label: 'Food Preparer/Server', value: 'AHL' },
                { label: 'Manager', value: 'AHM' },
                { label: 'Other - Hotel Services', value: 'AHR' },
                { label: 'Other - Food Services', value: 'AHS' }
              ]
            },
            {
              industry: 'Government/Military',
              occupations: [
                { label: 'Military - Enlisted (E1 - E4)', value: 'AJ9' },
                { label: 'Military - Warrant Officer (W1 - W5)', value: 'AJF' },
                { label: 'US Postal Worker', value: 'AJM' },
                { label: 'Auditor/Analyst/Examiner', value: 'AJN' },
                { label: 'Military - Officer (E5 - O10)', value: 'AJP' },
                { label: 'Supervisor/Manager', value: 'AJQ' },
                { label: 'Other - Government/Military', value: 'AJZ' }
              ]
            },
            {
              industry: 'Information Technology',
              occupations: [
                { label: 'Analyst', value: 'AK1' },
                { label: 'Director/Administrator', value: 'AK3' },
                { label: 'Computer Programmer', value: 'AKA' },
                { label: 'Supervisor/Manager', value: 'AKH' },
                { label: 'Technician', value: 'AKJ' },
                { label: 'Other - Information Technology', value: 'AKZ' }
              ]
            },
            {
              industry: 'Insurance',
              occupations: [
                { label: 'Agent/Broker', value: 'AL4' },
                { label: 'Claims Adjuster/Investigator', value: 'AL7' },
                { label: 'Supervisor/Manager', value: 'ALJ' },
                { label: 'Underwriter/Actuary', value: 'ALK' },
                { label: 'Other - Insurance', value: 'ALZ' }
              ]
            },
            {
              industry: 'Legal/Law Enforcement/Security',
              occupations: [
                { label: 'Correctional or Probation Officer', value: 'AM4' },
                { label: 'Legal Support Staff', value: 'AMG' },
                { label: 'Security Guard', value: 'AMN' },
                { label: 'Lawyer/Judge/Mediator', value: 'AMQ' },
                { label: 'Fire Fighter', value: 'AMR' },
                { label: 'Police/Highway Patrol/Sheriff', value: 'AMS' },
                { label: 'Detective/Investigator', value: 'AMT' },
                { label: 'Supervisor/Manager', value: 'AMU' },
                { label: 'Other - Legal/Law Enf/Security', value: 'AMZ' }
              ]
            },
            {
              industry: 'Medical/Social Services/Religion',
              occupations: [
                { label: 'Chiropractor', value: 'AN2' },
                { label: 'Clergy', value: 'AN3' },
                { label: 'Licensed Nurse (LPN)', value: 'ANE' },
                { label: 'Registered Nurse (RN)', value: 'ANF' },
                { label: 'Paramedic/E.M.T.', value: 'ANK' },
                { label: 'Pharmacist', value: 'ANL' },
                { label: 'Social Worker', value: 'ANQ' },
                { label: 'Veterinarian', value: 'ANU' },
                { label: 'Aide/Attendant', value: 'ANV' },
                { label: 'Assistant/Technician/Therapist', value: 'ANW' },
                { label: 'Dentist/Orthodontist', value: 'ANY' },
                { label: 'Doctor/Surgeon', value: 'BN0' },
                { label: 'Psychologist/Therapist', value: 'BN1' },
                {
                  label: 'Other - Medical/Social Services/Religion',
                  value: 'BNZ'
                }
              ]
            },
            {
              industry: 'Personal Care/Service',
              occupations: [
                { label: 'Child or Elderly Care Provider', value: 'AP0' },
                { label: 'Dry Cleaning/Laundry Servicer', value: 'AP1' },
                { label: 'Hair/Personal Care Provider', value: 'AP2' },
                { label: 'Cleaner/Housekeeper', value: 'AP3' },
                { label: 'Pet Service Provider', value: 'AP7' },
                { label: 'Other - Personal Care/Service', value: 'APZ' }
              ]
            },
            {
              industry: 'Production/Manufacturing',
              occupations: [
                { label: 'Machine Operator', value: 'AQ7' },
                { label: 'Assembler/Fabricator', value: 'AQJ' },
                { label: 'Inspector/Quality Control', value: 'AQK' },
                { label: 'Supervisor/Manager', value: 'AQP' },
                { label: 'Other - Production/Manufacturing', value: 'AQZ' }
              ]
            },
            {
              industry: 'Repair/Maintenance/Grounds',
              occupations: [
                { label: 'Custodian/Janitor', value: 'AR1' },
                { label: 'Landscape/Grounds Maintenance', value: 'AR7' },
                { label: 'Laborer/Helper', value: 'ARA' },
                { label: 'Repair/Mechanic', value: 'ARB' },
                { label: 'Other - Repair/Maintenance/Grounds', value: 'ARZ' }
              ]
            },
            {
              industry: 'Sports/Recreation',
              occupations: [
                { label: 'Athlete', value: 'AS3' },
                { label: 'Admin/Activity Assistant', value: 'ASJ' },
                { label: 'Fitness Trainer/Instructor', value: 'ASK' },
                { label: 'Supervisor/Manager', value: 'ASL' },
                { label: 'Other - Sports/Recreation', value: 'ASZ' }
              ]
            },
            {
              industry: 'Travel/Transportation/Storage',
              occupations: [
                { label: 'Driver (All Vehicles)', value: 'ATQ' },
                { label: 'Packer/Mover', value: 'ATR' },
                { label: 'Supervisor/Manager', value: 'ATS' },
                { label: 'Other - Travel/Transportation/Storage', value: 'ATZ' }
              ]
            },
            {
              industry: 'Other',
              occupations: []
            }
          ],
          previousCompany: [
            { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
            { carrier: 'ACCC', indicator: 'N' },
            { carrier: 'ACUITY', indicator: 'S' },
            { carrier: 'ALLIED', indicator: 'S' },
            { carrier: 'ALLSTATE', indicator: 'S' },
            { carrier: 'AMERICAN BANKERS', indicator: 'N' },
            { carrier: 'AMERICAN FAMILY', indicator: 'S' },
            { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
            { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
            { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
            { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
            { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
            { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
            { carrier: 'AUTO-OWNERS', indicator: 'S' },
            { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
            { carrier: 'BRISTOL WEST', indicator: 'N' },
            { carrier: 'C N A INS GROUP', indicator: 'S' },
            { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
            { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
            { carrier: 'CLEARCOVER', indicator: 'N' },
            { carrier: 'COMMERCE', indicator: 'S' },
            { carrier: 'COUNTRY INS', indicator: 'S' },
            { carrier: 'CSE INS GROUP', indicator: 'S' },
            { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
            { carrier: 'DAIRYLAND', indicator: 'N' },
            { carrier: 'ESURANCE', indicator: 'S' },
            { carrier: 'FARM BUREAU', indicator: 'S' },
            { carrier: 'FARMERS', indicator: 'S' },
            { carrier: "FIREMAN'S FUND", indicator: 'S' },
            { carrier: 'FOUNDERS', indicator: 'N' },
            { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
            { carrier: 'GAINSCO', indicator: 'N' },
            { carrier: 'GEICO', indicator: 'S' },
            { carrier: 'GMAC', indicator: 'N' },
            { carrier: 'HAGGERTY', indicator: 'S' },
            { carrier: 'HARTFORD', indicator: 'S' },
            { carrier: 'INFINITY', indicator: 'N' },
            { carrier: 'KINGSWAY', indicator: 'N' },
            { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
            { carrier: 'MENDOTA', indicator: 'N' },
            { carrier: 'MERCURY', indicator: 'S' },
            { carrier: 'METLIFE', indicator: 'S' },
            { carrier: 'METROMILE', indicator: 'S' },
            { carrier: 'MGA INS CO INC', indicator: 'N' },
            { carrier: 'MILE AUTO', indicator: 'S' },
            { carrier: 'NATIONWIDE', indicator: 'S' },
            { carrier: 'OHIO CASULATY', indicator: 'S' },
            { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
            { carrier: 'OTHER STANDARD', indicator: 'S' },
            { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
            { carrier: 'PORSCHE AUTO', indicator: 'S' },
            { carrier: 'PROGRESSIVE', indicator: 'S' },
            { carrier: 'ROOT', indicator: 'S' },
            { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
            { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
            { carrier: 'SAFECO', indicator: 'S' },
            { carrier: 'SAFEWAY INS', indicator: 'N' },
            { carrier: 'STATE AUTO', indicator: 'S' },
            { carrier: 'STATE FARM', indicator: 'S' },
            { carrier: 'TOPA INS CO', indicator: 'S' },
            { carrier: 'TRAVELERS', indicator: 'S' },
            { carrier: 'UNIGARD', indicator: 'S' },
            { carrier: 'USAA', indicator: 'S' },
            { carrier: 'VIKING INS', indicator: 'N' }
          ]
        },
        previousCompany: [
          { carrier: 'AAA/AUTO CLUB', indicator: 'S' },
          { carrier: 'ACCC', indicator: 'N' },
          { carrier: 'ACUITY', indicator: 'S' },
          { carrier: 'ALLIED', indicator: 'S' },
          { carrier: 'ALLSTATE', indicator: 'S' },
          { carrier: 'AMERICAN BANKERS', indicator: 'N' },
          { carrier: 'AMERICAN FAMILY', indicator: 'S' },
          { carrier: 'AMERICAN FINANCIAL', indicator: 'S' },
          { carrier: 'AMERICAN NATIONAL', indicator: 'S' },
          { carrier: 'AMERICAN RELIABLE', indicator: 'N' },
          { carrier: 'ARIZONA AUTOMOBILE INS CO', indicator: 'S' },
          { carrier: 'ASSURANCE AMERICA', indicator: 'N' },
          { carrier: 'AUSTIN MUTUAL INS CO', indicator: 'S' },
          { carrier: 'AUTO-OWNERS', indicator: 'S' },
          { carrier: 'BADGER MUTUAL INS CO', indicator: 'S' },
          { carrier: 'BRISTOL WEST', indicator: 'N' },
          { carrier: 'C N A INS GROUP', indicator: 'S' },
          { carrier: 'CALIFORNIA STATE AUTO ASSOCIATION', indicator: 'S' },
          { carrier: 'CENTRAL MUTUAL INS CO', indicator: 'S' },
          { carrier: 'CLEARCOVER', indicator: 'N' },
          { carrier: 'COMMERCE', indicator: 'S' },
          { carrier: 'COUNTRY INS', indicator: 'S' },
          { carrier: 'CSE INS GROUP', indicator: 'S' },
          { carrier: 'CWI - DEPLOYED MILITARY', indicator: 'N' },
          { carrier: 'DAIRYLAND', indicator: 'N' },
          { carrier: 'ESURANCE', indicator: 'S' },
          { carrier: 'FARM BUREAU', indicator: 'S' },
          { carrier: 'FARMERS', indicator: 'S' },
          { carrier: "FIREMAN'S FUND", indicator: 'S' },
          { carrier: 'FOUNDERS', indicator: 'N' },
          { carrier: 'FREEDOM NATIONAL', indicator: 'N' },
          { carrier: 'GAINSCO', indicator: 'N' },
          { carrier: 'GEICO', indicator: 'S' },
          { carrier: 'GMAC', indicator: 'N' },
          { carrier: 'HAGGERTY', indicator: 'S' },
          { carrier: 'HARTFORD', indicator: 'S' },
          { carrier: 'INFINITY', indicator: 'N' },
          { carrier: 'KINGSWAY', indicator: 'N' },
          { carrier: 'LIBERTY MUTUAL', indicator: 'S' },
          { carrier: 'MENDOTA', indicator: 'N' },
          { carrier: 'MERCURY', indicator: 'S' },
          { carrier: 'METLIFE', indicator: 'S' },
          { carrier: 'METROMILE', indicator: 'S' },
          { carrier: 'MGA INS CO INC', indicator: 'N' },
          { carrier: 'MILE AUTO', indicator: 'S' },
          { carrier: 'NATIONWIDE', indicator: 'S' },
          { carrier: 'OHIO CASULATY', indicator: 'S' },
          { carrier: 'OTHER NON-STANDARD', indicator: 'N' },
          { carrier: 'OTHER STANDARD', indicator: 'S' },
          { carrier: 'PHOENIX INDEMNITY INS CO', indicator: 'N' },
          { carrier: 'PORSCHE AUTO', indicator: 'S' },
          { carrier: 'PROGRESSIVE', indicator: 'S' },
          { carrier: 'ROOT', indicator: 'S' },
          { carrier: 'ROYAL & SUN ALLIANCE', indicator: 'S' },
          { carrier: 'SAFE AUTO INS CO', indicator: 'N' },
          { carrier: 'SAFECO', indicator: 'S' },
          { carrier: 'SAFEWAY INS', indicator: 'N' },
          { carrier: 'STATE AUTO', indicator: 'S' },
          { carrier: 'STATE FARM', indicator: 'S' },
          { carrier: 'TOPA INS CO', indicator: 'S' },
          { carrier: 'TRAVELERS', indicator: 'S' },
          { carrier: 'UNIGARD', indicator: 'S' },
          { carrier: 'USAA', indicator: 'S' },
          { carrier: 'VIKING INS', indicator: 'N' }
        ]
      },
      agencyCodes: [
        {
          name: 'ANSWER',
          dev: false,
          code: '33001-1'
        },
        {
          name: 'GABI',
          dev: false,
          code: '13025-1'
        },
        {
          name: 'GOTRANS',
          dev: false,
          code: '13002-2'
        },
        {
          name: 'INSURIFY',
          dev: false,
          code: '13021-1'
        },
        {
          name: 'PORSCHE',
          dev: true,
          code: '33008-1'
        },
        {
          name: 'PORSCHE',
          dev: false,
          code: '13022-2'
        },
        {
          name: 'ZEBRA',
          dev: false,
          code: '13008-1'
        }
      ],
      coverage: {
        AZ: {
          bodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000'
          ],
          collisionDeductible: ['$500', '$1,000', '$1,500', '$2,500', '$5,000'],
          propertyDamageDeductible: [
            '$250',
            '$500',
            '$1,000',
            '$1,500',
            '$2,000',
            '$2,500',
            '$5,000'
          ],
          propertyDamage: [
            '$25,000',
            '$50,000',
            '100 CSL',
            '$100,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          underinsuredMotorist: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredBodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredPropertyDamage: [
            '$25,000',
            '$50,000',
            '100 CSL',
            '$100,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          personalInjuryProtection: [
            '$500',
            '$1,000',
            '$2,000',
            '$2,500',
            '$5,000',
            '$10,000',
            '$25,000',
            'I decline this coverage'
          ],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$125/$3,750',
            'I decline this coverage'
          ]
        },
        CA: {
          bodilyInjury: [
            '$15,000/$30,000',
            '$25,000/$50,000',
            '$30,000/$60,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000'
          ],
          collisionDeductible: ['$500', '$1,000', '$1,500', '$2,500', '$5,000'],
          propertyDamage: [
            '$5,000',
            '$10,000',
            '$25,000',
            '$50,000',
            '100 CSL',
            '$100,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          uninsuredBodilyInjury: [
            '$15,000/$30,000',
            '$25,000/$50,000',
            '$30,000/$60,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredPropertyDamage: ['$3,500'],
          personalInjuryProtection: [
            '$1,000',
            '$2,000',
            '$5,000',
            '$10,000',
            '$25,000',
            'I decline this coverage'
          ],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$75/$2,250',
            '$100/$3,000',
            '$125/$3,750',
            'I decline this coverage'
          ]
        },
        CO: {
          bodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$750',
            '$1,000',
            '$1,500',
            '$2,000'
          ],
          collisionDeductible: ['$500', '$750', '$1,000', '$1,500', '$2,000'],
          propertyDamage: [
            '$15,000',
            '$25,000',
            '100 CSL',
            '$50,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          propertyDamageDeductible: ['$100', '$250', '$500', '$1000', '$2500'],
          uninsuredBodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          personalInjuryProtection: ['$5,000', '$10,000', '$25,000'],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$60/$1,800',
            '$125/$3,750',
            'I decline this coverage'
          ],
          towingCoverage: ['Roadside Assistance', 'I decline this coverage']
        },
        FL: {
          bodilyInjury: [
            '$10,000/$20,000',
            '$25,000/$50,000',
            '$50,000/$100,000',
            '$100,000/$300,000',
            '$300,000/$300,000',
            '$250,000/$500,000',
            '$500,000/$500,000',
            'I decline this coverage'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000',
            '$10,000',
            '$20,000',
            '$50,000',
            '$100,000'
          ],
          collisionDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000',
            '$10,000',
            '$20,000',
            '$50,000',
            '$100,000'
          ],
          propertyDamage: [
            '$10,000',
            '$20,000',
            '$25,000',
            '$50,000',
            '$100,000',
            '$250,000'
          ],
          uninsuredBodilyInjury: [
            '$10,000/$20,000',
            '$25,000/$50,000',
            '$50,000/$100,000',
            '$100,000/$300,000',
            '$300,000/$300,000',
            '$250,000/$500,000',
            '$500,000/$500,000'
          ],
          medicalPayments: ['$1,000', '$2,000', '$5,000', '$10,000'],
          personalInjuryProtection: [
            '$10,000',
            '$10,000',
            '$10,000',
            '$10,000',
            '$10,000',
            '$10,000',
            '$10,000',
            '$10,000'
          ],
          pipDeductible: ['$0', '$250', '$500', '$1,000'],
          pipDeductibleType: [
            'Excluded - Named Insured',
            'Excluded - Named Insured & Resident Relative',
            'Included - Named Insured',
            'Included - Named Insured & Resident Relative'
          ],
          rentalReimbursement: [
            '$50/$1,500',
            '$60/$1,800',
            '$120/$3,600',
            'I decline this coverage'
          ]
        },
        GA: {
          bodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '$100,000/$300,000',
            '$250,000/$500,000'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000'
          ],
          collisionDeductible: ['$500', '$1,000', '$1,500', '$2,500', '$5,000'],
          propertyDamageDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000'
          ],
          propertyDamage: ['$25,000', '$50,000', '$100,000', '$100,000'],
          uninsuredCoverageType: ['Added On', 'Reduced By'],
          uninsuredBodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '$100,000/$300,000',
            '$250,000/$500,000'
          ],
          uninsuredPropertyDamage: [
            '$25,000',
            '$50,000',
            '$100,000',
            '$100,000'
          ],
          personalInjuryProtection: [
            '$500',
            '$1,000',
            '$2,000',
            '$2,500',
            '$5,000'
          ],
          rentalReimbursement: [
            '$125/$3,750',
            '$125/$3,750',
            '$125/$3,750',
            '$125/$3,750'
          ]
        },
        IL: {
          bodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredBodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,000'
          ],
          collisionDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,000'
          ],
          propertyDamage: [
            '$20,000',
            '$25,000',
            '100 CSL',
            '$50,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          propertyDamageDeductible: ['$100', '$250', '$500', '$1000', '$2500'],
          uninsuredPropertyDamage: ['$50,000', '$100,000', '$100,000'],
          personalInjuryProtection: [
            '$1,000',
            '$2,000',
            '$2,500',
            '$5,000',
            '$10,000',
            '$25,000'
          ],
          rentalReimbursement: ['$50/$1,500', '$60/$1,800', '$125/$3,750']
        },
        OH: {
          bodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000'
          ],
          collisionDeductible: ['$500', '$1,000', '$1,500', '$2,500', '$5,000'],
          propertyDamageDeductible: ['$250'],
          propertyDamage: [
            '$25,000',
            '$50,000',
            '100 CSL',
            '$100,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          uninsuredBodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredPropertyDamage: [
            '$7,500',
            '$10,000',
            '$10,000',
            '$10,000',
            '$25,000',
            '$25,000',
            '$25,000'
          ],
          personalInjuryProtection: [
            '$500',
            '$1,000',
            '$2,000',
            '$5,000',
            '$10,000',
            'I decline this coverage'
          ],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$60/$1,800',
            '$125/$3,750',
            'I decline this coverage'
          ],
          towingCoverage: ['Roadside Assistance', 'I decline this coverage']
        },
        OR: {
          bodilyInjury: [
            '$50,000/$100,000',
            '$100,000/$300,000',
            '$250,000/$500,000'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,000',
            '$2,500'
          ],
          collisionDeductible: ['$500', '$1,000', '$1,500', '$2,000', '$2,500'],
          propertyDamage: ['$50,000', '$100,000', '$100,000'],
          uninsuredBodilyInjury: [
            '$50,000/$100,000',
            '$100,000/$300,000',
            '$250,000/$500,000'
          ],
          uninsuredPropertyDamage: ['$20,000', '$50,000', '$50,000'],
          personalInjuryProtection: ['$25,000', '$50,000', '$100,000'],
          rentalReimbursement: ['$125/$3,750', '$125/$3,750', '$125/$3,750']
        },
        PA: {
          combinedFirstParty: ['$177,500', 'I decline this coverage'],
          accidentalDeath: [
            '$5,000',
            '$15,000',
            '$25,000',
            'I decline this coverage'
          ],
          funeralBenefits: ['$1,500', '$2,500', 'I decline this coverage'],
          incomeLoss: [
            '$1,000/$5,000',
            '$1,000/$15,000',
            '$1,500/$25,000',
            '$2,500/$50,000',
            'I decline this coverage'
          ],
          extraBenefits: [
            '$1.1M less $100,000 Ded w/ Work Comp',
            'I decline this coverage'
          ],
          tortOptions: ['Full', 'Limited'],
          bodilyInjury: [
            '$15,000/$30,000',
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000',
            '$10,000',
            '$20,000',
            '$50,000'
          ],
          collisionDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000',
            '$10,000',
            '$20,000',
            '$50,000'
          ],
          propertyDamage: [
            '$5,000',
            '$15,000',
            '$25,000',
            '100 CSL',
            '$50,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          underinsuredMotorist: [
            '$15,000/$30,000',
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredBodilyInjury: [
            '$15,000/$30,000',
            '$25,000/$50,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredPropertyDamage: [
            '$5,000',
            '$15,000',
            '$25,000',
            '100 CSL',
            '$50,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          personalInjuryProtection: [
            '$5,000',
            '$10,000',
            '$25,000',
            '$50,000',
            '$100,000'
          ],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$125/$3,750',
            'I decline this coverage'
          ]
        },
        TN: {
          bodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '$100,000 CSL',
            '$100,000/$300,000',
            '$300,000 CSL',
            '$250,000/$500,000',
            '$500,000 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$750',
            '$1,000',
            '$1,500',
            '$2,000',
            '$10,000',
            '$20,000',
            '$50,000',
            'I decline this coverage'
          ],
          collisionDeductible: [
            '$500',
            '$750',
            '$1,000',
            '$1,500',
            '$2,000',
            '$10,000',
            '$20,000',
            '$50,000',
            'I decline this coverage'
          ],
          propertyDamage: [
            '$25,000',
            '$25,000',
            '$100,000 CSL',
            '$50,000',
            '$300,000 CSL',
            '$100,000',
            '$500,000 CSL'
          ],
          uninsuredBodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '$100,000 CSL',
            '$100,000/$300,000',
            '$300,000 CSL',
            '$250,000/$500,000',
            '$500,000 CSL',
            'I decline this coverage'
          ],
          uninsuredPropertyDamage: [
            '$25,000',
            '$25,000',
            '$100,000 CSL',
            '$50,000',
            '$300,000 CSL',
            '$100,000',
            '$500,000 CSL',
            'I decline this coverage'
          ],
          personalInjuryProtection: [
            '$250',
            '$500',
            '$1,000',
            '$2,000',
            '$5,000',
            '$10,000',
            'I decline this coverage'
          ],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$125/$3,750',
            'I decline this coverage'
          ]
        },
        TX: {
          bodilyInjury: [
            '$30,000/$60,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000'
          ],
          collisionDeductible: ['$500', '$1,000', '$1,500', '$2,500', '$5,000'],
          propertyDamageDeductible: ['$250'],
          propertyDamage: [
            '$25,000',
            '$25,000',
            '100 CSL',
            '$50,000',
            '300 CSL',
            '$100,000',
            '500 CSL'
          ],
          uninsuredBodilyInjury: [
            '$30,000/$60,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredPropertyDamage: [
            '$25,000',
            '$25,000',
            '$50,000',
            '$50,000',
            '$50,000',
            '$100,000',
            '$100,000'
          ],
          personalInjuryProtection: [
            '$2,500',
            '$5,000',
            '$10,000',
            'I decline this coverage'
          ],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$60/$1,800',
            '$125/$3,750',
            'I decline this coverage'
          ],
          towingCoverage: ['Roadside Assistance', 'I decline this coverage']
        },
        UT: {
          bodilyInjury: [
            '$30,000/$65,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          comprehensiveDeductible: [
            '$100',
            '$250',
            '$500',
            '$750',
            '$1,000',
            '$1,500',
            '$2,000'
          ],
          collisionDeductible: [
            '$100',
            '$250',
            '$500',
            '$750',
            '$1,000',
            '$1,500',
            '$2,000'
          ],
          propertyDamage: [
            '$25,000',
            '$50,000',
            '100 CSL',
            '$100,000',
            '300 CSL',
            '500 CSL'
          ],
          propertyDamageDeductible: [
            '$100',
            '$250',
            '$500',
            '$750',
            '$1,000',
            '$1,500',
            '$2,000',
            '$2,500',
            '$5,000'
          ],
          uninsuredBodilyInjury: [
            '$30,000/$65,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          underinsuredMotorist: [
            '$30,000/$65,000',
            '$50,000/$100,000',
            '100 CSL',
            '$100,000/$300,000',
            '300 CSL',
            '$250,000/$500,000',
            '500 CSL'
          ],
          uninsuredPropertyDamage: ['$3,500'],
          personalInjuryProtection: ['$3,000', '$5,000', '$10,000'],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$60/$1,800',
            'I decline this coverage'
          ]
        },
        WI: {
          bodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '$125,000 CSL',
            '$100,000/$300,000',
            '$300,000 CSL',
            '$250,000/$500,000',
            '$500,000 CSL'
          ],
          uninsuredBodilyInjury: [
            '$25,000/$50,000',
            '$50,000/$100,000',
            '$125,000 CSL',
            '$100,000/$300,000',
            '$300,000 CSL',
            '$250,000/$500,000',
            '$500,000 CSL'
          ],
          comprehensiveDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000',
            '$10,000',
            '$20,000',
            '$50,000',
            'I decline this coverage'
          ],
          collisionDeductible: [
            '$500',
            '$1,000',
            '$1,500',
            '$2,500',
            '$5,000',
            '$10,000',
            '$20,000',
            '$50,000',
            'I decline this coverage'
          ],
          propertyDamage: [
            '$10,000',
            '$15,000',
            '$25,000',
            '$50,000',
            '$125,000 CSL',
            '$100,000',
            '$300,000 CSL',
            '$250,000',
            '$500,000 CSL'
          ],
          underinsuredMotorist: [
            '$50,000/$100,000',
            '$125,000 CSL',
            '$100,000/$300,000',
            '$300,000 CSL',
            '$250,000/$500,000',
            '$500,000 CSL',
            'I decline this coverage'
          ],
          personalInjuryProtection: [
            '$1,000',
            '$2,000',
            '$5,000',
            '$10,000',
            'I decline this coverage'
          ],
          rentalReimbursement: [
            '$40/$1,200',
            '$50/$1,500',
            '$125/$3,750',
            'I decline this coverage'
          ]
        }
      }
    }
  },
  getters: {
    activeNav: state => state.activeNav,
    activeUserNav: state => state.activeUserNav,
    isLoggedIn: state => state.isLoggedIn,
    isRollover: state => state.isRollover,
    authStatus: state => state.status,
    user: state => state.user,
    accountIdentifier: state => state.accountIdentifier,
    policies: state => state.policies,
    selectedPolicy: state => state.selectedPolicy,
    selectedVehicle: state => state.selectedVehicle,
    quoteForm: state => state.quoteForm,
    quoteResponse: state => state.quoteResponse,
    bindResponse: state => state.bindResponse,
    quoteRequest: state => state.quoteRequest,
    vehicleImageResponse: state => state.vehicleImageResponse,
    lienHolderResponses: state => state.lienHolderResponses,
    selectsInfo: state => state.selects.info,
    selectsVehicles: state => state.selects.vehicles,
    selectsDrivers: state => state.selects.drivers,
    selectsAgencyCodes: state => state.selects.agencyCodes,
    selectsCoverage: state => state.selects.coverage,
    loader: state => state.loader,
    isThisSquish: () => isThisSquish,
    isFloat: () => isFloat,
    roundAmountTo: () => roundAmountTo,
    // Per-vehicle estimated monthly cost. `includeUnlimited` toggles the
    // unlimited-rate branch: the unlimited-capable flow passes `true`, while
    // the state-specific coverage components call without it to preserve their
    // pay-per-mile (estMonthlyNoFee) behavior.
    getMonthlyCostForIndividualVehicle: state => (
      itemIndex,
      includeUnlimited = false
    ) => {
      if (
        state.quoteResponse &&
        state.quoteResponse.policies[0] &&
        state.quoteResponse.policies[0].vehicleInfo
      ) {
        const vehicle = state.quoteResponse.policies[0].vehicleInfo[itemIndex];
        if (
          includeUnlimited &&
          state.quoteForm.vehicles[itemIndex] &&
          state.quoteForm.vehicles[itemIndex].unlimitedVehicle
        ) {
          return roundAmountTo(vehicle.unlimitedRate, 2);
        }
        return roundAmountTo(vehicle.premiumInfo.estMonthlyNoFee, 2);
      }
    }
  },
  actions: {
    requestVinCheck(_, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            process.env.VUE_APP_API_URL + 'vin/check',
            JSON.stringify({
              vin: data.vin,
              state: data.state ? data.state : ''
            }),
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Origin: 'https://www-staging.porscheautoinsurance.com',
                Referer: 'https://www-staging.porscheautoinsurance.com'
              },
              responseType: 'json'
            }
          )
          .then(response => {
            resolve(response);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    requestPolicyDocs(_, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(API_URL + 'documents/link', data, {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(resp => {
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    requestDocsLinks(_, data) {
      return new Promise((resolve, reject) => {
        axios
          .get(API_URL + `documents?token=${data.token}&id=${data.token}`, {
            headers: {
              Accept: 'application/json'
            },
            responseType: 'json'
          })
          .then(resp => {
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    toggleNav({ commit }, forceClose) {
      commit('toggleNav', forceClose);
    },
    toggleUserNav({ commit }, forceClose) {
      commit('toggleUserNav', forceClose);
    },
    logIn({ commit }, data) {
      return new Promise((resolve, reject) => {
        commit('setAuthStatus', 'loading');
        axios
          .post(
            API_URL + 'user/login_with_password',
            JSON.stringify({
              accountIdentifier: data.accountIdentifier,
              password: data.password
            }),
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              responseType: 'json'
            }
          )
          .then(resp => {
            commit('authSuccess', resp.data);
            resolve(resp);
          })
          .catch(err => {
            commit('authError');
            reject(err);
          });
      });
    },
    logOut({ commit }) {
      return new Promise((resolve, reject) => {
        commit('logOut');
        commit('toggleNav', true);
        axios
          .post(
            API_URL + 'user/clear_session',
            {},
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              responseType: 'json'
            }
          )
          .then(resp => {
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    magicLogin(_, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            API_URL + 'user/login_with_magic_link',
            JSON.stringify({
              accountIdentifier: data.accountIdentifier,
              returnUrl: data.returnUrl
            }),
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              responseType: 'json'
            }
          )
          .then(resp => {
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    signUp(_, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            API_URL + 'user/signup',
            JSON.stringify({
              policyNumber: data.policyAddress,
              emailAddress: data.emailAddress,
              returnUrl: data.returnUrl,
              password: data.password
            }),
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              responseType: 'json'
            }
          )
          .then(resp => {
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    checkSession({ commit }, { token, currentRoutePath }) {
      return new Promise((resolve, reject) => {
        let url;
        if (token) {
          url = API_URL + 'user/retrieve_session?accountIdentifier=' + token;
        } else {
          url = API_URL + 'user/retrieve_session';
        }
        commit('setAuthStatus', 'loading');
        axios
          .get(
            url,
            {},
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              responseType: 'json'
            }
          )
          .then(resp => {
            commit('updateSessionStatus', true);
            commit('updateUserData', resp.data);
            commit('setAuthStatus', 'success');
            if (
              currentRoutePath === '/receive-a-quote' &&
              token &&
              resp.data &&
              resp.data.policies &&
              resp.data.policies[0]
            ) {
              commit('updateQuoteResponse', { policies: resp.data.policies });
            }
            resolve(true);
          })
          .catch(() => {
            commit('updateSessionStatus', false);
            commit('setAuthStatus', 'error');
            reject(false);
          });
      });
    },
    requestQuote({ commit }, data) {
      return new Promise(resolve => {
        axios
          .post(API_URL + 'policy/quote', JSON.stringify(data), {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(resp => {
            commit('updateQuoteResponse', resp.data);
            resolve(resp);
          })
          .catch(err => {
            resolve(err && err.response && err.response.data);
          });
      });
    },
    bindQuote({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(API_URL + 'policy/bind', JSON.stringify(data), {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(resp => {
            commit('updateBindResponse', resp.data);
            this.dispatch('checkSession', {
              token: null,
              currentRoutePath: null
            });
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    saveQuote({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(API_URL + 'user/update', JSON.stringify(data), {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(resp => {
            commit('updateQuoteInUser', resp.data);
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    fetchQuote({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(API_URL + 'policy/lookup_quote', JSON.stringify(data), {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(resp => {
            commit('updateQuoteResponse', resp.data);
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    fetchPrefillQuote({ commit }, token) {
      const url = `https://${
        process.env.VUE_APP_ENV === 'Production' ? 'api-secondary' : 'staging'
      }.porscheautoinsurance.com/api/quote/prefill?token=${token}`;
      return new Promise((resolve, reject) => {
        axios
          .get(url, {
            withCredentials: true,
            headers: {
              Accept: 'application/json'
            },
            responseType: 'json'
          })
          .then(resp => {
            commit('updateQuoteFromSerializeResponse', resp.data);
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    retrieveRolloverData({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            API_URL + 'policy/rollover',
            JSON.stringify({
              token: data
            }),
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              responseType: 'json'
            }
          )
          .then(resp => {
            commit('confirmRollover', resp);
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    updateSingleLienholder(_, data) {
      return axios
        .post(API_URL + 'policy/update_lienholder', JSON.stringify(data), {
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          responseType: 'json'
        })
        .catch(err => {
          throw err;
        });
    },
    resetPassword({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            API_URL + 'user/reset_password',
            JSON.stringify({
              emailAddress: data.email,
              returnUrl: data.returnUrl
            }),
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              responseType: 'json'
            }
          )
          .then(resp => {
            commit('resetPaswordSuccess');
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    createPassword({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            API_URL + 'user/change_password_with_token',
            JSON.stringify(data),
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              responseType: 'json'
            }
          )
          .then(resp => {
            commit('createPaswordSuccess');
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    changePassword({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(API_URL + 'user/change_password', JSON.stringify(data), {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(resp => {
            commit('changePasswordSuccess');
            resolve(resp);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    getVehicleImage({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .create({ withCredentials: false })
          .get(
            VEHICLE_IMG_BASE_URL +
              'image-urls-vin-api/v1/' +
              data.vin.toUpperCase(),
            {},
            {
              headers: {
                Accept: 'application/xml',
                'Content-Type': 'application/xml'
              },
              responseType: 'xml'
            }
          )
          .then(resp => {
            var vehicleImageData = xmljs.xml2json(resp.data, {
              compact: true,
              spaces: 4
            });
            vehicleImageData = JSON.parse(vehicleImageData);
            if (
              vehicleImageData &&
              vehicleImageData.Vin &&
              vehicleImageData.Vin.picture
            ) {
              if (data.inAccountPage) {
                commit('updateAccountVehicleImageResponse', {
                  vehicleImageData: vehicleImageData,
                  index: data.index
                });
              } else {
                commit('updateVehicleImageResponse', vehicleImageData);
              }
              resolve(resp);
            } else {
              this.dispatch('getVehicleImageByYMM', data);
            }
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    getVehicleImageByYMM({ commit }, data) {
      return new Promise((resolve, reject) => {
        axios
          .create({ withCredentials: false })
          .get(
            VEHICLE_IMG_BASE_URL +
              'image-urls-api/v1/modelyear/' +
              data.year +
              '/modelrange/' +
              data.model,
            {},
            {
              headers: {
                Accept: 'application/xml',
                'Content-Type': 'application/xml'
              },
              responseType: 'xml'
            }
          )
          .then(resp => {
            var vehicleImageData = xmljs.xml2json(resp.data, {
              compact: true,
              spaces: 4
            });
            if (data.inAccountPage) {
              commit(
                'updateAccountVehicleImageResponse',
                JSON.parse(vehicleImageData),
                data.index
              );
            } else {
              commit(
                'updateVehicleImageResponse',
                JSON.parse(vehicleImageData)
              );
            }
            resolve(resp);
          })
          .catch(err => {
            if (err && err.response && err.response.status === 404) {
              commit('updateVehicleImageResponse', null);
            }
            reject(err);
          });
      });
    },
    mailChimpFormSubmit(_, { url, data }) {
      return new Promise((resolve, reject) => {
        Vue.prototype.$jQuery.ajax({
          type: 'GET',
          url: url,
          data: data,
          cache: false,
          dataType: 'json',
          contentType: 'application/json; charset=utf-8',
          error: err => {
            reject(err);
          },
          success: data => {
            if (data.result !== 'success') {
              reject(data);
            } else {
              resolve(data);
            }
          }
        });
      });
    },
    contactFormSubmit(_, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(API_URL + 'contact/submit', data, {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(() => {
            resolve(true);
          })
          .catch(err => reject(err));
      });
    },
    subscribeFormSubmit(_, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(API_URL + 'contact/subscribe', data, {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(() => {
            resolve(true);
          })
          .catch(err => reject(err));
      });
    },
    claimsFormSubmit(_, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(API_URL + 'contact/claims', data, {
            withCredentials: true,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            responseType: 'json'
          })
          .then(() => {
            resolve(true);
          })
          .catch(err => reject(err));
      });
    }
  },
  mutations: {
    confirmRollover(state, payload) {
      if (
        payload &&
        !_.isEmpty(payload) &&
        payload.data.documents_url !== null &&
        payload.data.esignature_url !== null
      ) {
        Vue.set(state, 'isRollover', true);
      }
    },
    updateQuoteResponse(state, payload) {
      if (
        payload &&
        payload.policies &&
        payload.policies[0].quoteRequest &&
        !_.isEmpty(payload.policies[0].quoteRequest) &&
        !state.quoteRequest
      ) {
        let incomingQuoteRequest = payload.policies[0].quoteRequest;
        let infoForm = {
          firstName: incomingQuoteRequest.insuredFirstName,
          lastName: incomingQuoteRequest.insuredLastName,
          streetAddressOne: incomingQuoteRequest.addresses[0].streetName,
          zipCode: incomingQuoteRequest.addresses[0].zip,
          city: incomingQuoteRequest.addresses[0].city,
          creditScore: incomingQuoteRequest.creditScore
            ? incomingQuoteRequest.creditScore
            : 0,
          estimatedCreditScore: incomingQuoteRequest.creditScore
            ? state.selects.info.estimatedCreditScore.find(
                obj => obj.value === incomingQuoteRequest.creditScore
              )
            : null,
          state: state.selects.info.states.find(
            state => state.value === incomingQuoteRequest.addresses[0].state
          ),
          isGarageSameAsMailing: incomingQuoteRequest.isGarageSameAsMailing
            ? state.selects.info.isGarageSameAsMailing[0]
            : state.selects.info.isGarageSameAsMailing[1],
          garagingStreetAddressOne: !incomingQuoteRequest.isGarageSameAsMailing
            ? incomingQuoteRequest.addresses[1].streetName
            : null,
          garagingZipCode: !incomingQuoteRequest.isGarageSameAsMailing
            ? incomingQuoteRequest.addresses[1].zip
            : null,
          garagingCity: !incomingQuoteRequest.isGarageSameAsMailing
            ? incomingQuoteRequest.addresses[1].city
            : null,
          garagingState: !incomingQuoteRequest.isGarageSameAsMailing
            ? state.selects.info.states.find(
                state => state.value === incomingQuoteRequest.addresses[1].state
              )
            : null,
          dateOfBirth: moment(
            incomingQuoteRequest.drivers.find(driver => {
              if (driver.relation === 'INSURED') {
                return driver;
              }
            }).dob
          ).format('MM/DD/YYYY'),
          mobilePhoneNumber: incomingQuoteRequest.phoneNumbers.find(contact => {
            if (contact.type === 'Mobile') {
              return contact;
            }
          }).number,
          preferredContactNumber: incomingQuoteRequest.phoneNumbers.find(
            contact => {
              return contact.type === 'Other';
            }
          ).number,
          hearAbout: state.selects.info.hearAbout.find(
            hearAbout => hearAbout.value === incomingQuoteRequest.referral
          ),
          dealer:
            incomingQuoteRequest.referral === 'Dealer'
              ? incomingQuoteRequest.referralDealer
              : null,
          isPCAMember: state.selects.info.PCAMemberOptions.find(
            obj => obj.value === incomingQuoteRequest.isInPorscheClubOfAmerica
          )
        };
        Vue.set(state.quoteForm, 'info', infoForm);
        if (
          incomingQuoteRequest.vehicles !== undefined &&
          incomingQuoteRequest.vehicles !== null
        ) {
          state.quoteForm.vehicles = [];
          let vehicleLength = incomingQuoteRequest.vehicles.length;
          let purposeOpts = [
            {
              label: 'Commute to NJ or NY.',
              value: 'Commute'
            },
            {
              label: 'Personal',
              value: 'Pleasure'
            },
            {
              label: 'Business',
              value: 'Business'
            }
          ];

          incomingQuoteRequest.vehicles.forEach((vehicle, index) => {
            let vehicleObj = {
              id: index + 1,
              vin: vehicle.vin,
              purpose: purposeOpts.find(
                purpose => purpose.value === vehicle.mileage.usageType
              )
                ? purposeOpts.find(
                    purpose => purpose.value === vehicle.mileage.usageType
                  )
                : null,
              miles: vehicle.mileage.miles,
              priorDamage:
                state.selects.vehicles.priorDamage.find(
                  priorDamageOpt => priorDamageOpt.value === vehicle.priorDamage
                ) || state.selects.vehicles.priorDamage[0],
              leaseOwnFinance: state.selects.vehicles.leaseOwnFinance.find(
                financeOpt =>
                  financeOpt.value.toUpperCase() === vehicle.financing
              )
                ? state.selects.vehicles.leaseOwnFinance.find(
                    financeOpt =>
                      financeOpt.value.toUpperCase() === vehicle.financing
                  )
                : null,
              statedValue: vehicle.statedValue
                ? vehicle.statedValue
                    .toString()
                    .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
                : '',
              unlimitedVehicle: vehicle.billingType === 'capped'
            };
            vehicleObj.statedValue = '$' + vehicleObj.statedValue;
            vehicleObj.vin = vehicleObj.vin.toUpperCase();
            vehicleObj.showMiles = vehicleObj.purpose.value === 'Pleasure';
            new Promise((resolve, reject) => {
              axios
                .post(
                  API_URL + 'vin/check',
                  JSON.stringify({
                    vin: vehicle.vin,
                    state: state.quoteForm.info.state
                      ? state.quoteForm.info.state.value
                      : ''
                  }),
                  {
                    withCredentials: true,
                    headers: {
                      Accept: 'application/json',
                      'Content-Type': 'application/json'
                    },
                    responseType: 'json'
                  }
                )
                .then(resp => {
                  this.vinData = resp.data;
                  vehicleObj.isVINValid = true;
                  vehicleObj.year = this.vinData.year;
                  vehicleObj.make = this.vinData.make;
                  vehicleObj.model = this.vinData.model;
                  vehicleObj.style = this.vinData.bodyStyle;
                  if (vehicleLength === index + 1) {
                    let data = {
                      vin: vehicleObj.vin,
                      year: this.vinData.year,
                      make: this.vinData.make
                    };
                    this.dispatch('getVehicleImage', data);
                  }
                  resolve(true);
                })
                .catch(err => {
                  if (err.response.status === 404) {
                    vehicleObj.isVINValid = false;
                    vehicleObj.year = null;
                    vehicleObj.make = null;
                    vehicleObj.model = null;
                    vehicleObj.style = null;
                  }
                  reject(false);
                });
            });
            state.quoteForm.vehicles.push(vehicleObj);
          });

          let coverageObj = {
            bodilyInjury: incomingQuoteRequest.limits.find(
              policyDetail => policyDetail.name === 'BI'
            )
              ? incomingQuoteRequest.limits.find(
                  policyDetail => policyDetail.name === 'BI'
                ).limit
              : '',
            comprehensiveDeductible: [],
            collisionDeductible: [],
            propertyDamageDeductible: [],
            uninsuredCoverageType: incomingQuoteRequest.limits.find(
              detail => detail.name === 'UM'
            )
              ? incomingQuoteRequest.limits.find(detail => detail.name === 'UM')
                  .limit
              : '',
            uninsuredBodilyInjury: incomingQuoteRequest.limits.find(
              detail => detail.name === 'UMUIMBI'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'UMUIMBI'
                ).limit
              : '',
            propertyDamage: incomingQuoteRequest.limits.find(
              detail => detail.name === 'PD'
            )
              ? incomingQuoteRequest.limits
                  .find(detail => detail.name === 'PD')
                  .limit.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
              : '',
            personalInjuryProtection: incomingQuoteRequest.limits.find(
              detail => detail.name === 'PIP' || detail.name === 'MP'
            )
              ? incomingQuoteRequest.limits
                  .find(detail => detail.name === 'PIP' || detail.name === 'MP')
                  .limit.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
              : '',
            rentalReimbursement: [],
            policyEffectiveDate: moment(
              incomingQuoteRequest.effectiveDate
            ).format('MM/DD/YYYY')
          };
          if (moment() > moment(incomingQuoteRequest.effectiveDate)) {
            coverageObj.policyEffectiveDate = moment().format('MM/DD/YYYY');
          }
          coverageObj.propertyDamage = '$' + coverageObj.propertyDamage;
          if (incomingQuoteRequest.addresses[0].state === 'AZ') {
            coverageObj.uninsuredBodilyInjury = incomingQuoteRequest.limits.find(
              detail => detail.name === 'UMBI'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'UMBI'
                ).limit
              : '';
          }
          if (incomingQuoteRequest.addresses[0].state === 'CA') {
            coverageObj.uninsuredBodilyInjury = incomingQuoteRequest.limits.find(
              detail => detail.name === 'UMBI'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'UMBI'
                ).limit
              : '';
          }
          if (incomingQuoteRequest.addresses[0].state === 'FL') {
            coverageObj.uninsuredBodilyInjury = incomingQuoteRequest.limits.find(
              detail => detail.name === 'UMBI'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'UMBI'
                ).limit
              : '';
            coverageObj.pipDeductible = incomingQuoteRequest.limits.find(
              detail => detail.name === 'PIPDeductible'
            )
              ? incomingQuoteRequest.limits
                  .find(detail => detail.name === 'PIPDeductible')
                  .limit.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
              : '';
            let deductibleType = incomingQuoteRequest.limits.find(
              detail => detail.name === 'PIPDeductibleType'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'PIPDeductibleType'
                ).limit
              : null;
            let workloss = incomingQuoteRequest.limits.find(
              detail => detail.name === 'WORKLOSS'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'WORKLOSS'
                ).limit
              : null;
            if (deductibleType && workloss) {
              deductibleType =
                deductibleType === 'NIRR'
                  ? 'Named Insured & Resident Relative'
                  : 'Named Insured';
              workloss =
                workloss[0] + workloss.substring(1).toLowerCase() + 'd - ';
              coverageObj.pipDeductibleType = workloss + deductibleType;
            }
            coverageObj.medicalPayments = incomingQuoteRequest.limits.find(
              detail => detail.name === 'MP'
            )
              ? incomingQuoteRequest.limits
                  .find(detail => detail.name === 'MP')
                  .limit.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
              : '';
            coverageObj.uninsuredCoverageType =
              coverageObj.uninsuredCoverageType === 'S'
                ? 'Stacked'
                : 'Unstacked';
          }
          if (incomingQuoteRequest.addresses[0].state === 'TN') {
            coverageObj.uninsuredBodilyInjury = incomingQuoteRequest.limits.find(
              detail => detail.name === 'UMBI'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'UMBI'
                ).limit
              : '';
          }
          if (incomingQuoteRequest.addresses[0].state === 'TX') {
            coverageObj.uninsuredBodilyInjury = incomingQuoteRequest.limits.find(
              detail => detail.name === 'UMBI'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'UMBI'
                ).limit
              : '';
          }
          if (incomingQuoteRequest.addresses[0].state === 'WI') {
            coverageObj.uninsuredBodilyInjury = incomingQuoteRequest.limits.find(
              detail => detail.name === 'UMBI'
            )
              ? incomingQuoteRequest.limits.find(
                  detail => detail.name === 'UMBI'
                ).limit
              : '';
          }
          if (
            incomingQuoteRequest.addresses[0].state === 'GA' ||
            incomingQuoteRequest.addresses[0].state === 'OH'
          ) {
            let vehicleUmpdLimit = incomingQuoteRequest.vehicles[0].coverageLimits.find(
              limit => limit.name === 'UMUIMPD'
            );
            coverageObj.uninsuredPropertyDamage = !_.isEmpty(vehicleUmpdLimit)
              ? vehicleUmpdLimit.limit.replace(
                  /(\d)(?=(\d\d\d)+(?!\d))/g,
                  '$1,'
                )
              : '';
          } else {
            coverageObj.uninsuredPropertyDamage = !_.isEmpty(
              incomingQuoteRequest.limits.find(detail => detail.name === 'UMPD')
            )
              ? incomingQuoteRequest.limits
                  .find(detail => detail.name === 'UMPD')
                  .limit.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
              : '';
          }
          coverageObj.personalInjuryProtection =
            '$' + coverageObj.personalInjuryProtection;
          coverageObj.uninsuredBodilyInjury = coverageObj.uninsuredBodilyInjury
            .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
            .split('/')
            .map(curr => '$' + curr)
            .join('/');
          coverageObj.bodilyInjury = coverageObj.bodilyInjury
            .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
            .split('/')
            .map(curr => '$' + curr)
            .join('/');
          incomingQuoteRequest.vehicles.forEach(vehicle => {
            let comp = vehicle.coverageLimits.find(
              coverage => coverage.name === 'COMP'
            );
            if (!_.isEmpty(comp)) {
              let formattedVal = comp.limit.replace(
                /(\d)(?=(\d\d\d)+(?!\d))/g,
                '$1,'
              );
              coverageObj.comprehensiveDeductible.push('$' + formattedVal);
            } else {
              coverageObj.comprehensiveDeductible.push(
                'I decline this coverage'
              );
            }

            let coll = vehicle.coverageLimits.find(
              coverage => coverage.name === 'COLL'
            );
            if (!_.isEmpty(coll)) {
              let formattedVal = coll.limit.replace(
                /(\d)(?=(\d\d\d)+(?!\d))/g,
                '$1,'
              );
              coverageObj.collisionDeductible.push('$' + formattedVal);
            } else {
              if (
                coverageObj.collisionDeductible[
                  coverageObj.collisionDeductible.length - 1
                ] !== 'I decline this coverage'
              ) {
                coverageObj.collisionDeductible.push('I decline this coverage');
              }
            }

            if (
              !_.isEmpty(state.quoteForm.info.state) &&
              state.quoteForm.info.state.value === 'GA'
            ) {
              let umuimpdded = vehicle.coverageLimits.find(
                coverage => coverage.name === 'UMUIMPDDeductible'
              );
              if (!_.isEmpty(umuimpdded)) {
                let formattedVal = umuimpdded.limit.replace(
                  /(\d)(?=(\d\d\d)+(?!\d))/g,
                  '$1,'
                );
                coverageObj.propertyDamageDeductible.push('$' + formattedVal);
              }
            }

            let rental = vehicle.coverageLimits.find(
              coverage => coverage.name === 'RENT'
            );
            if (!_.isEmpty(rental)) {
              let formattedVal = rental.limit
                .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
                .split('/')
                .map(curr => '$' + curr)
                .join('/');
              coverageObj.rentalReimbursement.push(formattedVal);
            } else {
              if (
                coverageObj.rentalReimbursement[
                  coverageObj.rentalReimbursement.length - 1
                ] !== 'I decline this coverage'
              ) {
                coverageObj.rentalReimbursement.push('I decline this coverage');
              }
            }
          });
          Vue.set(state.quoteForm, 'coverage', coverageObj);
        }
        if (
          incomingQuoteRequest.drivers !== undefined &&
          incomingQuoteRequest.drivers !== null
        ) {
          const structuredDrivers = restructureDriverObject(
            state,
            incomingQuoteRequest
          );
          structuredDrivers.forEach(driver => {
            let stateIndustries =
              state.selects.drivers[incomingQuoteRequest.ratedState] &&
              state.selects.drivers[incomingQuoteRequest.ratedState].occupation;
            if (stateIndustries) {
              let occuCode = driver.occupationCode.toString() || null;
              let occObj = null;

              stateIndustries.forEach(ind => {
                let foundOcc = ind.occupations.find(
                  occ => occ.value.toString() === occuCode
                );
                if (foundOcc) {
                  driver.occupationIndustry = {
                    label: ind.industry,
                    value: ind.industry
                  };
                  occObj = foundOcc;
                }
              });
              driver.occupation = occObj;
              driver.occupationCode = (occObj && occObj.value) || null;
            }
          });
          Vue.set(state.quoteForm, 'drivers', structuredDrivers);
        }
      }
      state.quoteResponse = payload;
    },
    updateQuoteFromSerializeResponse(state, payload) {
      Vue.set(state, 'external', true);
      if (
        payload &&
        !_.isEmpty(payload) &&
        !_.isEmpty(payload.quoteRequestDetails) &&
        !state.quoteRequest
      ) {
        const details = payload.quoteRequestDetails;
        const householdInfo = details.householdInfo || {};
        const mailingAddress = householdInfo.policyMailingAddress || {};
        const phoneNumbers = householdInfo.policyPhoneNumbers || [];
        const policyCoverages = details.policyCoverages || [];
        const drivers = details.drivers || [];
        const vehicles = details.vehicles || [];
        const ratedState = details.ratedState || mailingAddress.state;

        const policyCoverage = code =>
          policyCoverages.find(coverage => coverage.coverageCode === code);

        const primaryDriver =
          drivers.find(
            driver => driver.driverRelation === 'primary_named_insured'
          ) ||
          drivers[0] ||
          {};

        const primaryPhone =
          phoneNumbers.find(
            phone => phone.primary && phone.type === 'mobile'
          ) ||
          phoneNumbers.find(phone => phone.type === 'mobile') ||
          phoneNumbers.find(phone => phone.primary) ||
          phoneNumbers[0];

        const garagingZipCode =
          vehicles.length && vehicles[0].garagingZipCode
            ? vehicles[0].garagingZipCode
            : null;
        const isGarageSameAsMailing =
          !garagingZipCode || garagingZipCode === mailingAddress.zipCode;

        // ---------- INFO ----------
        const infoForm = {
          firstName: primaryDriver.firstName || null,
          lastName: primaryDriver.lastName || null,
          middleInitial: primaryDriver.middleInitial || '',
          streetAddressOne: mailingAddress.streetAddress || null,
          householdCount:
            householdInfo.numberOfHouseholdResidents != null
              ? householdInfo.numberOfHouseholdResidents
              : null,
          zipCode: mailingAddress.zipCode || null,
          city: mailingAddress.city || null,
          state: state.selects.info.states.find(
            opt => opt.value === (mailingAddress.state || ratedState)
          ),
          isGarageSameAsMailing: isGarageSameAsMailing
            ? state.selects.info.isGarageSameAsMailing[0]
            : state.selects.info.isGarageSameAsMailing[1],
          // The prefill schema only carries the garaging ZIP code, so the
          // remaining garaging address fields are intentionally left blank.
          garagingStreetAddressOne: null,
          garagingZipCode: isGarageSameAsMailing ? null : garagingZipCode,
          garagingCity: null,
          garagingState: null,
          dateOfBirth: primaryDriver.birthDate
            ? moment(primaryDriver.birthDate).format('MM/DD/YYYY')
            : null,
          mobilePhoneNumber: primaryPhone
            ? formatPrefillPhoneNumber(primaryPhone.phoneNumber)
            : null,
          emailAddress: householdInfo.policyEmail || null,
          creditScore: primaryDriver.estimatedCreditScore || 0,
          estimatedCreditScore: null,
          // hearAbout/agencyCode are not part of the prefill schema.
          hearAbout: null
        };
        if (primaryDriver.estimatedCreditScore) {
          // Map a numeric score to the closest descending bucket option.
          infoForm.estimatedCreditScore =
            state.selects.info.estimatedCreditScore.find(
              option => primaryDriver.estimatedCreditScore >= option.value
            ) || null;
        }
        const residencyOptions =
          infoForm.state &&
          state.selects.info[infoForm.state.value] &&
          state.selects.info[infoForm.state.value].lengthOfResidency;
        if (residencyOptions && householdInfo.yearsOfResidency != null) {
          infoForm.lengthOfResidency =
            Number(householdInfo.yearsOfResidency) >= 1
              ? residencyOptions.find(opt => opt.value === '12+')
              : residencyOptions.find(opt => opt.value === '0-11');
        }
        Vue.set(state.quoteForm, 'info', infoForm);

        // ---------- VEHICLES ----------
        const vinRegex = /[!*]/g;
        state.quoteForm.vehicles = [];
        vehicles.forEach((vehicle, index) => {
          const leaseOwnFinanceSearch = state.selects.vehicles.leaseOwnFinance.find(
            financeOpt =>
              vehicle.financing &&
              financeOpt.value.toUpperCase() === vehicle.financing.toUpperCase()
          );
          const usage = (vehicle.vehicleUsage || '').toUpperCase();
          const purposeSearch =
            usage === 'BUSINESS'
              ? state.selects.vehicles.purposes.find(
                  purpose => purpose.value === 'Business'
                )
              : state.selects.vehicles.purposes.find(
                  purpose => purpose.value === 'Pleasure'
                );
          const vehVinParse =
            vehicle.vin &&
            vehicle.vin.length === 17 &&
            isThisSquish(vehicle.vin)
              ? vehicle.vin.replace(vinRegex, '0').slice(0, 15)
              : vehicle.vin;
          const vehicleObj = {
            vinOrYmm: 'Year/Make/Model',
            id: index + 1,
            vin: null,
            year: vehicle.year || null,
            make: vehicle.make || null,
            bodyStyle: vehicle.style ? vehicle.style.trim() : null,
            model: vehicle.model || null,
            purpose: purposeSearch || null,
            miles: vehicle.mileage
              ? vehicle.mileage.estimatedAnnualMileage
              : null,
            priorDamage: vehicle.priorDamage
              ? {
                  label: 'Yes',
                  value: true
                }
              : {
                  label: 'No',
                  value: false
                },
            leaseOwnFinance: leaseOwnFinanceSearch || null
          };
          vehicleObj.showMiles =
            !!vehicleObj.purpose && vehicleObj.purpose.value === 'Pleasure';
          if (vehicle.vin) {
            new Promise((resolve, reject) => {
              axios
                .post(
                  API_URL + 'vin/check',
                  JSON.stringify({
                    vin: vehVinParse,
                    state: state.quoteForm.info.state
                      ? state.quoteForm.info.state.value
                      : ''
                  }),
                  {
                    withCredentials: true,
                    headers: {
                      Accept: 'application/json',
                      'Content-Type': 'application/json'
                    },
                    responseType: 'json'
                  }
                )
                .then(resp => {
                  const vinData = resp.data;
                  vehicleObj.isVINValid = !isThisSquish(vehicle.vin);
                  vehicleObj.vin = isThisSquish(vehicle.vin)
                    ? null
                    : vehicle.vin;
                  vehicleObj.year = vinData.year;
                  vehicleObj.make = vinData.make;
                  vehicleObj.bodyStyle = vinData.bodyStyle;
                  vehicleObj.model = vinData.model;
                  state.quoteForm.vehicles.push(vehicleObj);
                  resolve(true);
                })
                .catch(err => {
                  state.quoteForm.vehicles.push(vehicleObj);
                  reject(false, err);
                });
            });
          } else {
            state.quoteForm.vehicles.push(vehicleObj);
          }
        });

        // ---------- COVERAGE ----------
        const biCoverage = policyCoverage('BI');
        const pdCoverage = policyCoverage('PD');
        const umCoverage = policyCoverage('UM');
        const uimCoverage = policyCoverage('UIM');
        const firstVehicleCoverages =
          vehicles.length && vehicles[0].vehicleCoverages
            ? vehicles[0].vehicleCoverages
            : [];
        const policyUmpd = firstVehicleCoverages.find(
          coverage => coverage.coverageCode === 'UMPD'
        );

        const coverageObj = {
          bodilyInjury:
            biCoverage && biCoverage.limit
              ? formatSlashLimit(biCoverage.limit)
              : null,
          comprehensiveDeductible: [],
          collisionDeductible: [],
          propertyDamageDeductible: [],
          uninsuredBodilyInjury:
            umCoverage && umCoverage.limit
              ? formatSlashLimit(umCoverage.limit)
              : 'I decline this coverage',
          propertyDamage:
            pdCoverage && pdCoverage.limit
              ? formatSingleLimit(pdCoverage.limit)
              : null,
          personalInjuryProtection: null,
          rentalReimbursement: [],
          policyEffectiveDate: moment().format('MM/DD/YYYY'),
          uninsuredCoverageType: '',
          underinsuredMotorist:
            uimCoverage && uimCoverage.limit ? uimCoverage.limit : '',
          uninsuredPropertyDamage:
            policyUmpd && policyUmpd.limit
              ? formatSingleLimit(policyUmpd.limit)
              : null
        };
        if (umCoverage && umCoverage.limitDetails) {
          const umType = umCoverage.limitDetails.find(
            detail => detail.detailCode === 'UMType'
          );
          if (umType && umType.limit) {
            coverageObj.uninsuredCoverageType = umType.limit;
          }
        }
        if (
          details.effectiveDate &&
          !(moment() > moment(details.effectiveDate))
        ) {
          coverageObj.policyEffectiveDate = moment(
            details.effectiveDate
          ).format('MM/DD/YYYY');
        }

        // ---------- VEHICLE COVERAGE ----------
        vehicles.forEach(vehicle => {
          const vehicleCoverages = vehicle.vehicleCoverages || [];

          const comp = vehicleCoverages.find(
            coverage => coverage.coverageCode === 'COMP'
          );
          coverageObj.comprehensiveDeductible.push(
            comp && comp.limit && ratedState !== 'CA'
              ? formatSingleLimit(comp.limit)
              : 'I decline this coverage'
          );

          const coll = vehicleCoverages.find(
            coverage => coverage.coverageCode === 'COLL'
          );
          coverageObj.collisionDeductible.push(
            coll && coll.limit && ratedState !== 'CA'
              ? formatSingleLimit(coll.limit)
              : 'I decline this coverage'
          );

          if (ratedState === 'GA') {
            const vehicleUmpd = vehicleCoverages.find(
              coverage => coverage.coverageCode === 'UMPD'
            );
            const umpdDeductible =
              vehicleUmpd && vehicleUmpd.limitDetails
                ? vehicleUmpd.limitDetails.find(
                    detail => detail.detailCode === 'UMPDDed'
                  )
                : null;
            coverageObj.propertyDamageDeductible.push(
              umpdDeductible && umpdDeductible.limit
                ? formatSingleLimit(umpdDeductible.limit)
                : 'I decline this coverage'
            );
          }

          const rent = vehicleCoverages.find(
            coverage => coverage.coverageCode === 'RENT'
          );
          coverageObj.rentalReimbursement.push(
            rent && rent.limit
              ? formatSlashLimit(rent.limit)
              : 'I decline this coverage'
          );
        });
        Vue.set(state.quoteForm, 'coverage', coverageObj);

        // ---------- DRIVERS ----------
        if (drivers.length) {
          const structuredDrivers = restructureDriversFromQuoteRequest(
            state,
            details
          );
          Vue.set(state.quoteForm, 'drivers', structuredDrivers);
        }
      }
      state.quoteRequest = payload;
    },
    updateQuoteRequest(state, payload) {
      state.quoteRequest = payload;
    },
    updateForm(state, payload) {
      if (payload !== undefined && payload !== null) {
        let setQuoteResponseToNull = true;
        if (payload.info !== undefined && payload.info !== null) {
          if (_.isEqual(state.quoteForm.info, payload.info)) {
            setQuoteResponseToNull = false;
          } else {
            Vue.set(state.quoteForm, 'info', payload.info);
          }
        }
        if (payload.vehicles !== undefined && payload.vehicles !== null) {
          if (_.isEqual(state.quoteForm.vehicles, payload.vehicles)) {
            setQuoteResponseToNull = false;
          } else {
            let coverageDropDownOptions =
              state.selects.coverage[state.quoteForm.info.state.value];
            if (
              payload.vehicles[0].leaseOwnFinance.value === 'owned' &&
              coverageDropDownOptions.comprehensiveDeductible &&
              !coverageDropDownOptions.comprehensiveDeductible.includes(
                'I decline this coverage'
              )
            ) {
              coverageDropDownOptions.comprehensiveDeductible.push(
                'I decline this coverage'
              );
              coverageDropDownOptions.collisionDeductible.push(
                'I decline this coverage'
              );
            } else if (
              payload.vehicles[0].leaseOwnFinance.value !== 'owned' &&
              coverageDropDownOptions.comprehensiveDeductible &&
              coverageDropDownOptions.comprehensiveDeductible.includes(
                'I decline this coverage'
              )
            ) {
              coverageDropDownOptions.comprehensiveDeductible.pop();
              coverageDropDownOptions.collisionDeductible.pop();
            }
            let updatedCoverage = _.cloneDeep(state.quoteForm.coverage),
              coverageUpdated = false;
            for (let index = 0; index < payload.vehicles.length; index++) {
              if (
                payload.vehicles[index].leaseOwnFinance.value !== 'owned' &&
                !_.isEmpty(state.quoteForm.vehicles) &&
                state.quoteForm.vehicles[index] &&
                state.quoteForm.vehicles[index].leaseOwnFinance.value ===
                  'owned' &&
                updatedCoverage.comprehensiveDeductible[index] &&
                updatedCoverage.comprehensiveDeductible[index] ===
                  'I decline this coverage'
              ) {
                coverageUpdated = true;
                updatedCoverage.comprehensiveDeductible[index] = null;
                updatedCoverage.collisionDeductible[index] = null;
              }
              if (
                payload.vehicles[index].leaseOwnFinance &&
                payload.vehicles[index].leaseOwnFinance.value !== 'owned' &&
                !_.isEmpty(state.quoteForm.vehicles) &&
                state.quoteForm.info.state.value === 'GA' &&
                state.quoteForm.vehicles[index] &&
                state.quoteForm.vehicles[index].leaseOwnFinance &&
                state.quoteForm.vehicles[index].leaseOwnFinance.value ===
                  'owned'
              ) {
                coverageUpdated = true;
              }
            }
            if (coverageUpdated) {
              Vue.set(state.quoteForm, 'coverage', updatedCoverage);
            }
            Vue.set(state.quoteForm, 'vehicles', payload.vehicles);
          }
        }
        if (payload.drivers !== undefined && payload.drivers !== null) {
          if (_.isEqual(state.quoteForm.drivers, payload.drivers)) {
            setQuoteResponseToNull = false;
          } else {
            Vue.set(state.quoteForm, 'drivers', payload.drivers);
          }
        }
        if (payload.coverage !== undefined && payload.coverage !== null) {
          if (_.isEqual(state.quoteForm.coverage, payload.coverage)) {
            setQuoteResponseToNull = false;
          } else {
            Vue.set(state.quoteForm, 'coverage', payload.coverage);
          }
        }
        if (
          payload.lienHolderData !== undefined &&
          payload.lienHolderData !== null
        ) {
          setQuoteResponseToNull = false;
          Vue.set(state, 'lienHolderData', payload.lienHolderData);
        }
        if (state.quoteResponse && setQuoteResponseToNull) {
          Vue.set(state, 'quoteResponse', null);
        }
      } else {
        state.quoteForm = {
          info: {},
          vehicles: [],
          drivers: [],
          coverage: {}
        };
      }
    },
    toggleNav(state, forceClose) {
      if (forceClose) {
        state.activeNav = false;
      } else {
        state.activeNav = !state.activeNav;

        if (state.activeNav) {
          document.body.style.position = 'relative';
        } else {
          document.body.style.position = 'none';
        }
      }
    },
    toggleUserNav(state, forceClose) {
      if (forceClose) {
        state.activeUserNav = false;
      } else {
        state.activeUserNav = !state.activeUserNav;

        if (state.activeUserNav) {
          document.body.style.position = 'relative';
        } else {
          document.body.style.position = 'none';
        }
      }
    },
    setSelectedPolicy(state, newPolicy) {
      state.selectedPolicy = newPolicy;
    },
    updateSessionStatus(state, newStatus) {
      state.isLoggedIn = newStatus;
    },
    updateAccountIdentifier(state, newAccountIdentifier) {
      state.accountIdentifier = newAccountIdentifier;
    },
    updateUserData(state, newData) {
      state.status = 'success';
      state.user = newData.user;
      state.policies = newData.policies.filter(policy => policy);
      if (state.selectedPolicy === null && newData.policies.length >= 1) {
        state.selectedPolicy = newData.policies[0];
      }
    },
    setAuthStatus(state, newStatus) {
      state.status = newStatus;
    },
    authSuccess(state, data) {
      state.status = 'success';
      state.isLoggedIn = true;
      state.user = data.user;
      state.policies = data.policies.filter(policy => policy);
      if (data.policies.length === 1) {
        state.selectedPolicy = data.policies[0];
      }
    },
    authError(state) {
      state.status = 'error';
      state.isLoggedIn = false;
      state.user = {};
    },
    logOut(state) {
      state.status = '';
      state.isLoggedIn = false;
      state.user = {};
      state.accountIdentifier = '';
    },
    resetPaswordSuccess(state) {
      state.status = 'success';
    },
    createPaswordSuccess(state) {
      state.status = 'success';
    },
    changePaswordSuccess(state) {
      state.status = 'success';
    },
    updateVehicleImageResponse(state, data) {
      state.vehicleImageResponse = data;
    },
    updateAccountVehicleImageResponse(state, data) {
      state.selectedPolicy.vehicleInfo[data.index].vehicleImageResponse =
        data.vehicleImageData;
    },
    updateBindResponse(state, payload) {
      state.bindResponse = payload;
    },
    updateLienHolderResponse(state, payload) {
      state.lienHolderResponses.push(payload);
    },
    toggleLoader(state, data) {
      state.loader.isLoading = !state.loader.isLoading;
      if (data) {
        state.loader.loaderText = data.loaderText;
      }
    }
  }
});
